import { useState, useEffect, useRef } from 'react';
import ItemPickerModal from '../components/ItemPickerModal';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../api/client';
import { useAuth } from '../context/AuthContext';
import { usePermission } from '../context/PermissionContext';
import { deriveDetailSections } from '../utils/detailSections';
import { todayStr, datePlusDays, daysBetween } from '../utils/date';
import { VersionTabs, OldVersionBanner, VersionMemoCompare } from '../components/VersionTools';
import { matchAnyCI } from '../utils/search';
import SmartNumInput, { fmtNumericDisplay } from '../components/SmartNumInput';

const fmtN = (v) => { const n = String(v).replace(/[^0-9]/g,''); return n ? Number(n).toLocaleString() : ''; };
const pN = (v) => parseInt(String(v).replace(/[^0-9]/g,'')) || 0;
// 부호 허용 — 수기입력 항목의 음수 단가용
const pNS = (v) => { const s = String(v); const neg = s.trim().startsWith('-') ? -1 : 1; const n = parseInt(s.replace(/[^0-9]/g,''))||0; return neg * n; };
// 묶음 행 전용: 빈 문자열·텍스트를 false, 순수 숫자 문자열을 true 로 판별
const isBundleNum = (v) => { const s = String(v ?? '').trim(); return s !== '' && !isNaN(Number(s)); };
const sBadge = { draft:'badge-gray', pending_approval:'badge-yellow', published:'badge-blue', unapproved_published:'badge-orange' };
const sLabel = { draft:'임시저장', pending_approval:'승인대기', published:'발행', unapproved_published:'미승인발행', '':'신규' };

const TYPE_LABEL = { software:'Software', license:'License', hardware:'Hardware', appliance:'Appliance' };
const TYPE_SHORT = { software:'SW', license:'LIC', hardware:'HW', appliance:'AP' };
const TYPE_BADGE = { software:'badge-blue', license:'badge-purple', hardware:'badge-gray', appliance:'badge-green' };

// 합산 행(소계)의 구성 행: 합산 행 바로 위쪽의 같은 묶음 번호 연속 행 (이전 합산 행에서 멈춤)
const compositeMembersIdx = (its, ci) => {
  const b = its[ci].bundle || '';
  const out = [];
  for (let j = ci - 1; j >= 0; j--) {
    if (its[j].item_type === 'composite' || (its[j].bundle || '') !== b) break;
    out.unshift(j);
  }
  return out;
};

export default function EstimateNewPage() {
  const navigate = useNavigate();
  const { user: authUser, updateUserPreferences } = useAuth();
  const { canPerformAction } = usePermission();
  const canEdit = canPerformAction('제품 견적서 생성·수정');
  const isAdmin = authUser?.uid === 'U-0000';
  const [sp] = useSearchParams();
  const editId = sp.get('id');
  const fromId = sp.get('from');
  const copyId = sp.get('copy');
  const seedId = fromId || copyId;  // 데이터 시드 소스 — 두 모드 공통
  const initialBundleId = sp.get('bundle');

  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [publishing, setPublishing] = useState(false);

  // 기본정보
  const [info, setInfo] = useState(() => {
    return { estimate_date:todayStr(), valid_until:datePlusDays(todayStr(), 14), end_user:'', project_name:'', receiver_company:'', receiver_name:'', payment_terms:'고객사 결제 조건', delivery_terms:'발주 후 별도 협의', warranty_terms:'검수일로부터 12개월', notes:'- 유상 유지보수 요율 12% (DB 업데이트 및 장애시 지원, 점검 추가시 별도)\n- TAP, 부대장비, 케이블 추가 가격은 별도 (네트워크 환경에 따라 추가)', template_id:'' };
  });
  const [vatSep, setVatSep] = useState(true);
  const [vatInputMode, setVatInputMode] = useState('separate'); // 'separate' | 'inclusive' — vatSep=false 일 때만 유효
  const [showCP, setShowCP] = useState(true);
  const [showCPA, setShowCPA] = useState(false);
  // 공헌이익 시뮬레이션 입력값 (% 단위)
  const [techFeeRate, setTechFeeRate] = useState(0);
  const [salesFeeRate, setSalesFeeRate] = useState(0);
  // 작성자 결재선 (발행 전 미리보기)
  const [approvalChain, setApprovalChain] = useState([]);

  // 견적항목
  const [items, setItems] = useState([]);
  const [products, setProducts] = useState([]);
  const [allParts, setAllParts] = useState([]);
  const [showAddProd, setShowAddProd] = useState(false);
  const [showAddPart, setShowAddPart] = useState(false);
  const [addSearch, setAddSearch] = useState('');
  const [addType, setAddType] = useState('all');
  // 묶음 메타 캐시 — { [bundleId]: { name } }
  const [bundleMeta, setBundleMeta] = useState({});
  // Appliance 메타 캐시 — { [applianceProductId]: { name } } (펼친 자식 행의 lock 배지 표시용)
  const [applianceMeta, setApplianceMeta] = useState({});

  // 수신처 검색
  const [receiverSuggestions, setReceiverSuggestions] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [, setReceiverInputMode] = useState('text'); // text or select
  const debounceRef = useRef(null);

  // 견적서 양식 (템플릿)
  const [templates, setTemplates] = useState([]);

  // 승인
  const [showApproval, setShowApproval] = useState(false);
  const [approvalReason, setApprovalReason] = useState('');
  const [approvalInfo, setApprovalInfo] = useState(null);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [approvePassword, setApprovePassword] = useState('');
  const [rejectReasonText, setRejectReasonText] = useState('');
  const [approvalProcessing, setApprovalProcessing] = useState(false);

  const [memos, setMemos] = useState([]);
  const [memoText, setMemoText] = useState('');

  // 세부사항 편집
  const [editingDetail, setEditingDetail] = useState(null); // {idx, section}
  const [guideIdx, setGuideIdx] = useState(null); // 가이드금액(총판/협력사/최종사용자 단가) 툴팁이 열린 행
  useEffect(() => {
    if (guideIdx === null) return;
    const close = () => setGuideIdx(null);
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [guideIdx]);
  // 하드웨어사양 편집
  const [editingHwIdx, setEditingHwIdx] = useState(null);

  // 상태
  const [estId, setEstId] = useState(null);
  const [estStatus, setEstStatus] = useState('');
  const [draftId, setDraftId] = useState(null);
  const [estNumber, setEstNumber] = useState(null);
  const [authorId, setAuthorId] = useState(null);
  // 태그 (목록의 태그와 동일: 견적서당 1개). 저장 전 견적서는 선택값을 들고 있다가 첫 저장 직후 반영
  const [tagList, setTagList] = useState([]);
  const [tagId, setTagId] = useState('');
  const [pendingTag, setPendingTag] = useState(false);
  useEffect(() => { api.get('/settings/tags/all').then(setTagList).catch(()=>{}); }, []);
  useEffect(() => {
    if (!estId || !pendingTag) return;
    setPendingTag(false);
    api.put(`/estimates/${estId}/tags`, { tag_ids: tagId ? [Number(tagId)] : [] }).catch(e=>showToast(e.message));
  }, [estId, pendingTag]); // eslint-disable-line react-hooks/exhaustive-deps
  const changeTag = async (v) => {
    setTagId(v);
    if (!estId) { setPendingTag(true); return; }
    try { await api.put(`/estimates/${estId}/tags`, { tag_ids: v ? [Number(v)] : [] }); }
    catch (e) { showToast(e.message); }
  };
  // 공유 모달
  const [showShareModal, setShowShareModal] = useState(false);
  // 삭제 (임시저장은 확인만, 발행본은 비밀번호 확인)
  const [delModal, setDelModal] = useState(false);
  const [delPw, setDelPw] = useState('');
  const [delErr, setDelErr] = useState('');
  const [shareList, setShareList] = useState([]);
  const [shareQuery, setShareQuery] = useState('');
  const [shareSearch, setShareSearch] = useState([]);
  const shareSearchTimerRef = useRef(null);
  useEffect(() => {
    if (showShareModal && estId) api.get(`/estimates/${estId}/shares`).then(setShareList).catch(()=>{});
  }, [showShareModal, estId]);
  const [versions, setVersions] = useState([]);
  // '수량입력 강제' 상품구분 (상품구분 관리의 옵션). 상품의 상품구분(category, 라벨로 저장) 이름이거나,
  // 상품 유형(product_type: software/license/hardware/appliance)과 같은 이름('Hardware' 등)이면 강제 대상
  const [qtyForcedMap, setQtyForcedMap] = useState({});
  useEffect(() => {
    api.get('/settings/codes/product_category')
      .then(rows => setQtyForcedMap(Object.fromEntries((rows || []).filter(r => r.require_quantity)
        .flatMap(r => [[r.label, true], [String(r.label).toLowerCase(), true], [String(r.code || '').toLowerCase(), true]]))))
      .catch(() => {});
  }, []);
  const isQtyForcedProd = (pr) => !!pr && !!(
    (pr.category && qtyForcedMap[pr.category]) ||
    (pr.category && qtyForcedMap[String(pr.category).toLowerCase()]) ||
    (pr.product_type && qtyForcedMap[String(pr.product_type).toLowerCase()])
  );
  // 발행 후 수정(새 버전 없이): 서버가 알려주는 수정 가능 여부/남은 시간, 수정 모드, 남은 초 카운트다운
  const [editWindow, setEditWindow] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editRemain, setEditRemain] = useState(0);

  // 검증
  const [touched, setTouched] = useState({});
  const markTouched = (f) => setTouched(p=>({...p,[f]:true}));
  const saveErrors = [];
  const publishErrors = [];
  if (!info.estimate_date) publishErrors.push('견적일자');
  if (!info.valid_until) publishErrors.push('유효기간');
  if (!info.end_user) publishErrors.push('최종사용자');
  if (!info.project_name) publishErrors.push('사업명');
  if (!info.receiver_company) publishErrors.push('수신처');
  if (!info.receiver_name) publishErrors.push('수신자');
  if (!info.warranty_terms) publishErrors.push('무상하자보증기간');
  if (items.length === 0) publishErrors.push('견적 항목 (1개 이상)');
  const qtyMissing = items.filter(it => it._qtyUnset);
  if (qtyMissing.length > 0) publishErrors.push(`수량 입력 필요: ${qtyMissing.map(it => it.item_model || it.item_name).join(', ')}`);
  const fieldErr = (f,v) => touched[f] && !v ? { borderColor:'#ef4444', background:'#fef2f2' } : {};
  const fieldHint = (f,v,l) => touched[f] && !v ? <div style={{fontSize:11,color:'#ef4444',marginTop:3}}>⚠ {l}은(는) 필수입니다.</div> : null;

  const applyTemplate = (tpl) => {
    setInfo(prev => ({
      ...prev, template_id: String(tpl.id),
      payment_terms: tpl.payment_terms || prev.payment_terms,
      delivery_terms: tpl.delivery_terms || prev.delivery_terms,
      warranty_terms: tpl.warranty_terms || prev.warranty_terms,
      notes: tpl.notes ?? prev.notes,
      valid_until: datePlusDays(prev.estimate_date, tpl.valid_days || 14),
    }));
    setShowCP(tpl.show_consumer_price !== false);
    setShowCPA(tpl.show_consumer_amount === true);
    setVatSep(tpl.vat_separate !== false);
  };

  useEffect(() => {
    api.get('/products?status=active').then(setProducts);
    api.get('/parts').then(data => setAllParts(data.filter(p => p.purchase_price != null && p.is_part !== false)));
    api.get('/organizations/my-approval-chain').then(setApprovalChain).catch(()=>setApprovalChain([]));
    api.get('/settings/templates/all').then(data => {
      setTemplates(data);
      // 편집/후속/인용이 아닌 신규일 때 기본 템플릿 자동 선택 + 기본값 적용
      if (!editId && !seedId) {
        const def = data.find(t => t.is_default);
        if (def) applyTemplate(def);
      }
    });
  }, []);

  // 수신처 자동추천
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!info.receiver_company || info.receiver_company.length < 2 || selectedCustomer) {
      setReceiverSuggestions([]); return;
    }
    debounceRef.current = setTimeout(async () => {
      try {
        const data = await api.get(`/customers/search/${encodeURIComponent(info.receiver_company)}`);
        setReceiverSuggestions(data);
      } catch(e) {}
    }, 300);
  }, [info.receiver_company, selectedCustomer]);

  // 편집/후속/인용 로드
  useEffect(() => {
    const lid = editId || seedId;
    if (!lid) return;
    api.get(`/estimates/${lid}`).then(async est => {
      const srcDate = est.estimate_date?.slice(0,10) || '';
      const srcValid = est.valid_until?.slice(0,10) || '';
      let estimateDate = srcDate || todayStr();
      let validUntil = srcValid;
      // 후속버전: 작성일자를 항상 오늘로, 유효기간은 원본 유효일수를 유지해 오늘 기준으로 재조정
      if (fromId && !editId) {
        estimateDate = todayStr();
        let span = 14;
        if (srcDate && srcValid) { const d = daysBetween(srcDate, srcValid); if (d > 0) span = d; }
        validUntil = datePlusDays(estimateDate, span);
      }
      setInfo({ estimate_date:estimateDate, valid_until:validUntil, end_user:est.end_user||'', project_name:est.project_name||'', receiver_company:est.receiver_company||'', receiver_name:est.receiver_name||'', payment_terms:est.payment_terms||'', delivery_terms:est.delivery_terms||'', warranty_terms:est.warranty_terms||'', notes:est.notes||'', template_id:est.template_id||'' });
      setVatSep(est.vat_separate !== false);
      setVatInputMode(est.vat_input_mode === 'inclusive' ? 'inclusive' : 'separate');
      setShowCP(est.show_consumer_price !== false);
      setShowCPA(est.show_consumer_amount === true);
      setTechFeeRate(Number(est.tech_fee_rate) || 0);
      setSalesFeeRate(Number(est.sales_fee_rate) || 0);
      const loadedItems = [];
      for (const it of (est.items||[])) {
        const dov = it.display_overrides || {};
        const item = {
          ...it,
          unit_price: (it.bundle && dov.up != null) ? dov.up : String(it.unit_price||0),
          consumer_price: (it.bundle && dov.cp != null) ? dov.cp : (it.consumer_price ?? null),
          quantity: dov.qty_unset ? '' : ((it.bundle && dov.qty != null) ? dov.qty : (it.quantity ?? 1)),
          bundle: it.bundle||'', _editSpecs:it.specs||[], _editFeatures:it.features||[],
          _hide: dov.hide || {},
          _qtyUnset: !!dov.qty_unset, _qtyForced: !!dov.qty_unset,
        };
        if (it.item_type==='product' && it.product_id) { try { item._product = await api.get(`/products/${it.product_id}`); } catch(e) { item._product = null; } } // 상품이 삭제됐으면 null (견적 항목 자체 내용으로 표시)
        loadedItems.push(item);
      }
      setItems(loadedItems);
      if (editId) { setEditWindow(est.edit_window || null); setEditRemain(est.edit_window?.remaining_seconds || 0); setEstId(est.id); setEstStatus(est.status); setDraftId(est.draft_id); setEstNumber(est.estimate_number); setVersions(est.versions||[]); setApprovalInfo(est.approval||null); setAuthorId(est.created_by||null); setTagId(est.tags?.[0]?.id ? String(est.tags[0].id) : ''); api.get(`/estimates/${est.id}/memos`).then(setMemos).catch(()=>{}); }
    });
  }, [editId, seedId]);

  // 금액 계산
  // vat_input_mode 가 'inclusive' 인 경우 unit_price 는 사용자가 친 포함값.
  // 별도/VAT 환산은 합계 단계에서 1회만 수행 → 라운드트립 드리프트 제거.
  const isInclusiveStored = !vatSep && vatInputMode === 'inclusive';
  // 묶음 계산 규칙:
  //   • 공급가액/수량: 부모·자식 모두 자기 unit_price × 자기 수량 합산 (각 행 독립)
  //   • 원가: 부모/자식 모두 자기 cost_per_unit × 자기 수량 합산
  // 비묶음 행: 자기 unit_price/cost × 자기 수량
  const rawOf = (it, forComposite = false) => {
    // PDF 미포함 토글이 꺼진 항목은 공급가액에서 제외 (원가 / 공헌이익에는 영향)
    if (it.show_in_pdf === false) return 0;
    // '출력 제외'(공급금액 숨김) 행은 합계에서도 제외 (원가 / 공헌이익 분모에는 같은 기준 적용)
    // 단, 소계의 구성 행은 공급금액 칸이 소계에 병합되어 보이지 않으므로 '출력 제외' 여부와 상관없이 소계에 합산한다
    if (!forComposite && it._hide && it._hide.amt) return 0;
    // 묶음이든 아니든: 텍스트·빈값이면 0 기여 (숫자만 합산)
    if (!isBundleNum(it.unit_price)) return 0;
    if (it.bundle && !isBundleNum(it.quantity)) return 0;
    return pNS(it.unit_price) * (parseInt(it.quantity) || (it.bundle ? 0 : 1));
  };
  // 합산 행(소계): 바로 위 같은 묶음의 연속 행(구성 행)의 공급금액 합 = 합산 행의 공급가액. 구성 행은 총합계·원가 합계에서 제외.
  const memberSet = new Set();
  items.forEach((c, ci) => { if (c.item_type === 'composite') compositeMembersIdx(items, ci).forEach(j => memberSet.add(j)); });
  const useSplitQty = items.some((c, ci) => c.item_type === 'composite' && compositeMembersIdx(items, ci).length > 0); // 소계가 있으면 수량 칸을 둘로 나눈다 (구성 행 수량 | 소계 수량)
  const compositeUnit = (ci, its = items) => compositeMembersIdx(its, ci).reduce((a, j) => a + rawOf(its[j], true), 0);
  const compositeCostPerUnit = (ci, its = items) => compositeMembersIdx(its, ci).reduce((a, j) => a + (parseInt(its[j].cost_per_unit)||0) * (parseInt(its[j].quantity)||1), 0);
  const supplyOf = (it, idx) => {
    if (it.item_type === 'composite') return compositeUnit(idx) * (parseInt(it.quantity) || 1);
    return memberSet.has(idx) ? 0 : rawOf(it);
  };
  const costOf = (it, idx) => {
    if (it.item_type === 'composite') return compositeCostPerUnit(idx) * (parseInt(it.quantity)||1);
    return memberSet.has(idx) ? 0 : (parseInt(it.cost_per_unit)||0) * (parseInt(it.quantity)||1);
  };
  const rawSum = items.reduce((s,it,i) => s + supplyOf(it,i), 0);
  const subtotal = isInclusiveStored ? Math.round(rawSum / 1.1) : rawSum;
  const vat = isInclusiveStored ? (rawSum - subtotal) : Math.round(subtotal * 0.1);
  const total = isInclusiveStored ? rawSum : (subtotal + vat);
  const itemCostTotal = items.reduce((s,it,i) => s + costOf(it,i), 0);
  const techFee = Math.round(subtotal * (Number(techFeeRate)||0) / 100);
  const salesFee = Math.round(subtotal * (Number(salesFeeRate)||0) / 100);
  const costTotalWithFees = itemCostTotal + techFee + salesFee;
  const contribution = subtotal - costTotalWithFees;
  const contributionRate = subtotal > 0 ? (contribution / subtotal * 100) : 0;
  // 표시용: vatSep OFF + inclusive 모드면 v 가 이미 포함값 (×1.1 불필요). 그 외 vatSep OFF 면 ×1.1.
  const dispPrice = (v) => {
    if (vatSep) return v;
    if (isInclusiveStored) return v;
    return Math.round(v * 1.1);
  };
  const dispSubtotal = vatSep ? subtotal : total;

  const setValid = (d) => { setInfo({...info, valid_until:datePlusDays(info.estimate_date, d)}); };

  // 수신처 선택
  const selectCustomer = (cust) => {
    setInfo({...info, receiver_company:cust.name, receiver_name:cust.contact_name||''});
    setSelectedCustomer(cust);
    setReceiverSuggestions([]);
    setReceiverInputMode(cust.contact_name ? 'select' : 'text');
  };

  // 수신처 검색 모달
  const [showReceiverSearch, setShowReceiverSearch] = useState(false);
  const [receiverSearchResults, setReceiverSearchResults] = useState([]);
  const [receiverSearchQuery, setReceiverSearchQuery] = useState('');
  const searchReceiver = async () => {
    if (!receiverSearchQuery) return;
    try { setReceiverSearchResults(await api.get(`/customers/search/${encodeURIComponent(receiverSearchQuery)}`)); } catch(e) {}
  };

  // 즐겨찾기 (사용자 preferences 에 저장)
  const [favorites, setFavorites] = useState(() => {
    const f = authUser?.preferences?.favorites;
    return { products: f?.products || [], parts: f?.parts || [] };
  });
  useEffect(() => {
    const f = authUser?.preferences?.favorites;
    if (f) setFavorites({ products: f.products || [], parts: f.parts || [] });
  }, [authUser]);
  const toggleFavorite = async (kind, id) => {
    const list = favorites[kind] || [];
    const next = list.includes(id) ? list.filter(x => x !== id) : [...list, id];
    const merged = { ...favorites, [kind]: next };
    const prev = favorites;
    setFavorites(merged); // 낙관적 업데이트
    try {
      await api.put('/auth/preferences', { favorites: merged });
      // AuthContext 의 user 객체도 동기화 — 페이지 이동 후 useState 초기값이 올바르게 잡힘
      updateUserPreferences({ favorites: merged });
    } catch (e) {
      setFavorites(prev); // 롤백
      showToast('즐겨찾기 저장 실패: ' + (e?.message || ''));
    }
  };

  // 묶음 우클릭 컨텍스트 메뉴
  const [snippetCtxMenu, setSnippetCtxMenu] = useState(null); // {x, y}
  // 자주 쓰는 항목 통합 모달 (묶음/즐겨찾기/전체 3 탭)
  const [showItemPicker, setShowItemPicker] = useState(false);
  useEffect(() => {
    if (!snippetCtxMenu) return;
    const h = () => setSnippetCtxMenu(null);
    window.addEventListener('click', h);
    window.addEventListener('scroll', h, true);
    return () => { window.removeEventListener('click', h); window.removeEventListener('scroll', h, true); };
  }, [snippetCtxMenu]);

  // 우클릭 "이 묶음 저장" — 현재 항목들을 묶음 카탈로그에 새 묶음으로 저장 (디폴트 private)
  const saveCurrentAsBundle = async () => {
    if (items.length === 0) { alert('항목이 없습니다.'); return; }
    const name = window.prompt('묶음 이름:', '');
    if (!name || !name.trim()) return;
    try {
      await api.post('/bundles', {
        name: name.trim(),
        items: cleanItems(),
        visibility: 'private',
      });
      showToast(`묶음 "${name.trim()}" 저장됨 (비공개)`);
    } catch (e) { alert(e.message); }
  };

  // 견적 내 다음 사용 가능한 Appliance 묶음 라벨 — "A1", "A2", ... (기존 bundle 라벨과 충돌 안 함)
  const nextApplianceLabel = (its) => {
    const used = new Set(its.map(it => it.bundle).filter(Boolean));
    for (let n = 1; n < 1000; n++) {
      const label = `A${n}`;
      if (!used.has(label)) return label;
    }
    return `A${Date.now()}`;
  };

  // 외부 가격(상품/부품의 별도 단가) 을 현재 입력 모드에 맞게 변환
  const convPrice = (excValue) => {
    const v = Number(excValue || 0);
    return (!vatSep && vatInputMode === 'inclusive') ? Math.round(v * 1.1) : v;
  };
  // null 허용 버전 (consumer_price 처럼 비어 있을 수 있는 값용)
  const convPriceNullable = (v) => v == null ? null : convPrice(v);

  // Appliance 펼침 — 자식 N개를 행으로 풀고 같은 bundle 라벨/appliance_ref_id 부여
  const expandAppliance = async (p) => {
    const appl = await api.get(`/products/${p.id}`);
    if (!appl || !Array.isArray(appl.components) || appl.components.length === 0) {
      // 자식 없는 Appliance — 단일 행으로 fallback (이상 케이스)
      setItems(prev => [...prev, {
        item_type:'product', product_id:p.id, item_name:p.name, item_model:p.model,
        consumer_price:convPriceNullable(p.consumer_price), unit_price:String(convPrice(p.price_end_user||p.consumer_price||0)),
        quantity: isQtyForcedProd(p) ? '' : 1, _qtyUnset: isQtyForcedProd(p), _qtyForced: isQtyForcedProd(p),
        cost_per_unit:parseInt(p.total_cost)||0,
        main_title:appl?.main_title||'', sub_description:appl?.sub_description||'',
        sw_name:'', specs:[], features:[], hw_specs:{}, bundle:'',
        detail_sections: deriveDetailSections(appl),
        _product:appl, _editSpecs:[], _editFeatures:[],
      }]);
      showToast(`Appliance "${appl?.name||p.name}" 에 자식 상품이 없어 단일 행으로 추가됨`);
      return;
    }
    setApplianceMeta(prev => ({ ...prev, [p.id]: { name: appl.name } }));
    const label = nextApplianceLabel(items);
    // Appliance 본체 가격 — 0이 아니면 자식 가격을 무시하고 이 값을 그룹 가격으로 사용
    const apConsumer = Number(appl.consumer_price) || 0;
    const apEndUser  = Number(appl.price_end_user) || 0;
    const apDistr    = Number(appl.price_distributor) || 0;
    const apPartner  = Number(appl.price_partner) || 0;
    const apCost     = Number(appl.total_cost) || 0;
    const useAppl    = apEndUser > 0 || apConsumer > 0 || apDistr > 0 || apPartner > 0;
    const rows = [];
    for (let i = 0; i < appl.components.length; i++) {
      const c = appl.components[i];
      let childProd = null;
      try { childProd = await api.get(`/products/${c.child_product_id}`); }
      catch(e) { console.error('자식 상품 상세 로드 실패', c.child_product_id, e); }
      const ch = childProd || c;
      const isFirst = (i === 0);
      // Appliance 가격 정책: useAppl=true → 첫 자식에 Appliance 가격 몰빵, 나머지 0. 그룹 합계 = Appliance 가격.
      const rowConsumer = useAppl ? (isFirst ? convPriceNullable(apConsumer) : null) : convPriceNullable(ch.consumer_price);
      const rowUnit     = useAppl ? (isFirst ? String(convPrice(apEndUser || apConsumer || 0)) : '0')
                                  : String(convPrice(ch.price_end_user || ch.consumer_price || 0));
      const rowCost     = useAppl ? (isFirst ? apCost : 0) : (parseInt(ch.total_cost) || 0);
      // 가격설정정보(총판/협력사/최종사용자) 표시: useAppl → 모든 자식 행에 Appliance 가격 통일
      const productForDisplay = useAppl
        ? { ...(childProd || c), consumer_price: apConsumer, price_end_user: apEndUser, price_distributor: apDistr, price_partner: apPartner }
        : (childProd || c);
      // '수량입력 강제' 대상(Appliance 본체 또는 구성품 자신의 상품구분/유형)이면 구성 기본수량 대신 빈 칸으로 시작
      const qtyForced = isQtyForcedProd(p) || isQtyForcedProd(ch);
      rows.push({
        item_type:'product', product_id:c.child_product_id,
        item_name: ch.name, item_model: ch.model,
        consumer_price: rowConsumer,
        unit_price: rowUnit,
        quantity: qtyForced ? '' : (c.quantity || 1), _qtyUnset: qtyForced, _qtyForced: qtyForced,
        cost_per_unit: rowCost,
        main_title: ch.main_title || '',
        sub_description: ch.sub_description || '',
        sw_name: ch.sw_name || '',
        specs: ch.specs || [],
        features: ch.features || [],
        detail_sections: deriveDetailSections(ch),
        hw_specs: {},
        bundle: label,
        appliance_ref_id: p.id,
        _appliance_name: appl.name,
        _product: productForDisplay,
        _editSpecs: ch.specs || [],
        _editFeatures: ch.features || [],
      });
    }
    setItems(prev => [...prev, ...rows]);
    showToast(`Appliance "${appl.name}" (자식 ${rows.length}개) 추가됨 — 묶음 ${label}`);
  };

  // 제품 추가
  const addProduct = async (p) => {
    setShowAddProd(false); setAddSearch(''); setAddType('all');
    if (p.product_type === 'appliance') {
      await expandAppliance(p);
      return;
    }
    let prod = null;
    try { prod = await api.get(`/products/${p.id}`); } catch(e) {}
    setItems([...items, {
      item_type:'product', product_id:p.id, item_name:p.name, item_model:p.model,
      consumer_price:convPriceNullable(p.consumer_price), unit_price:String(convPrice(p.price_end_user||p.consumer_price||0)),
      quantity: isQtyForcedProd(p) ? '' : 1, _qtyUnset: isQtyForcedProd(p), _qtyForced: isQtyForcedProd(p),
      cost_per_unit:parseInt(p.total_cost)||0,
      main_title:prod?.main_title||'', sub_description:prod?.sub_description||'', sw_name:prod?.sw_name||'', specs:prod?.specs||[], features:prod?.features||[],
      detail_sections: deriveDetailSections(prod),
      hw_specs:{}, bundle:'', _product:prod, _editSpecs:prod?.specs||[], _editFeatures:prod?.features||[],
    }]);
  };

  const addPart = (p) => {
    setItems([...items, {
      item_type:'part', part_id:p.id, item_name:p.name, item_model:p.spec,
      consumer_price:null, unit_price:String(convPrice(p.purchase_price||0)),
      quantity:1, cost_per_unit:parseInt(p.purchase_price)||0,
      description:p.name+' '+(p.spec||''), bundle:'',
    }]);
    setShowAddPart(false); setAddSearch('');
  };

  const removeItem = (i) => {
    const it = items[i];
    if (it && it.bundle_ref_id) {
      if (!window.confirm('공식 묶음은 원자 단위입니다. 같은 묶음 행 전체가 함께 삭제됩니다. 진행할까요?')) return;
      setItems(items.filter(x => x.bundle_ref_id !== it.bundle_ref_id));
      return;
    }
    if (it && it.appliance_ref_id) {
      if (!window.confirm('Appliance 묶음은 원자 단위입니다. 같은 묶음 행 전체가 함께 삭제됩니다. 진행할까요?')) return;
      setItems(items.filter(x => !(x.appliance_ref_id === it.appliance_ref_id && x.bundle === it.bundle)));
      return;
    }
    setItems(items.filter((_,idx)=>idx!==i));
  };
  const upItem = (i,k,v) => setItems(items.map((it,idx)=>idx===i?{...it,[k]:v}:it));
  // 수량 변경: 수량 입력 강제 상품(_qtyForced 이거나 강제 상품구분의 상품)은 1 이상을 입력하면 '미입력' 해제, 비우면 다시 '미입력'
  const isQtyForced = (it) => !!(it._qtyForced || it._qtyUnset || isQtyForcedProd(it._product));
  const onQtyChange = (i, v) => setItems(items.map((it, idx) => idx === i
    ? { ...it, quantity: v, _qtyUnset: isQtyForced(it) ? !(parseInt(v) >= 1) : false } : it));
  // 행별 PDF 칸 표시/숨김 (k: cp | up | qty | amt). 화면 값·합계 계산은 그대로, PDF 에서만 빈 칸 처리.
  const toggleHide = (i,k) => setItems(items.map((it,idx)=>idx===i?{...it,_hide:{...(it._hide||{}),[k]:!(it._hide||{})[k]}}:it));
  // 칸 버튼을 길게 누르면: 그 칸을 토글하고, 같은 행의 네 칸(소비자단가·공급가액·수량·공급금액) 모두를 그 새 상태로 맞춤
  const HIDE_KEYS = ['cp','up','qty','amt'];
  const setRowHide = (i, value) => setItems(items.map((it,idx)=>idx===i?{...it,_hide:Object.fromEntries(HIDE_KEYS.map(k=>[k,value]))}:it));
  const LONG_PRESS_MS = 600;
  const longPress = useRef({ timer: null, fired: false });
  const hideBtn = (it,idx,k) => {
    if (ro) return null;
    const off = !!(it._hide||{})[k];
    const cancel = () => { clearTimeout(longPress.current.timer); longPress.current.timer = null; };
    const start = () => {
      cancel(); longPress.current.fired = false;
      longPress.current.timer = setTimeout(() => {
        longPress.current.fired = true;
        setRowHide(idx, !off);
        showToast(`이 행의 모든 칸을 ${!off ? '출력 제외로' : '출력 포함으로'} 변경했습니다.`);
      }, LONG_PRESS_MS);
    };
    return (
      <button type="button"
        onPointerDown={start} onPointerUp={cancel} onPointerLeave={cancel} onPointerCancel={cancel}
        onContextMenu={e=>e.preventDefault()}
        onClick={()=>{ if (longPress.current.fired) { longPress.current.fired = false; return; } toggleHide(idx,k); }}
        title="길게 눌러 일괄 변경"
        style={{display:'block',marginLeft:'auto',marginBottom:3,padding:'0 5px',fontSize:9,lineHeight:'15px',borderRadius:3,cursor:'pointer',fontWeight:700,userSelect:'none',WebkitUserSelect:'none',WebkitTouchCallout:'none',touchAction:'manipulation',
          border:`1px solid ${off?'#fca5a5':'#e2e8f0'}`,background:off?'#fef2f2':'#f8fafc',color:off?'#dc2626':'#94a3b8'}}>
        {off?'출력 제외':'출력 포함'}
      </button>
    );
  };

  // 항목 위/아래 이동. 같은 bundle 을 가진 연속된 행은 그룹째 함께 이동.
  const moveItem = (idx, dir) => {
    setItems(prev => {
      const bundle = prev[idx]?.bundle;
      let gs = idx, ge = idx;
      if (bundle) {
        while (gs > 0 && prev[gs-1].bundle === bundle) gs--;
        while (ge < prev.length-1 && prev[ge+1].bundle === bundle) ge++;
      }
      if (dir === -1) {
        if (gs === 0) return prev;
        const adj = prev[gs-1].bundle;
        let as = gs-1;
        if (adj) while (as > 0 && prev[as-1].bundle === adj) as--;
        return [...prev.slice(0, as), ...prev.slice(gs, ge+1), ...prev.slice(as, gs), ...prev.slice(ge+1)];
      }
      if (ge === prev.length-1) return prev;
      const adj = prev[ge+1].bundle;
      let ae = ge+1;
      if (adj) while (ae < prev.length-1 && prev[ae+1].bundle === adj) ae++;
      return [...prev.slice(0, gs), ...prev.slice(ge+1, ae+1), ...prev.slice(gs, ge+1), ...prev.slice(ae+1)];
    });
  };

  // 묶음 안에서만 행 위/아래 이동 (묶음을 벗어나려면 묶음을 해제해야 함)
  const moveWithinBundle = (idx, dir) => setItems(prev => {
    const j = idx + dir;
    if (j < 0 || j >= prev.length || !prev[idx].bundle || prev[j].bundle !== prev[idx].bundle) return prev;
    const u = [...prev]; [u[idx], u[j]] = [u[j], u[idx]]; return u;
  });
  const rowArrows = (idx) => {
    const b = items[idx].bundle;
    if (ro || !b || items.filter(x => x.bundle === b).length < 2) return null;
    const canUp = idx > 0 && items[idx-1].bundle === b;
    const canDown = idx < items.length-1 && items[idx+1].bundle === b;
    const st = (on) => ({cursor:on?'pointer':'default',opacity:on?0.7:0.2,color:'#64748b'});
    return (
      <div title="묶음 안에서 행 이동 (묶음 밖으로 옮기려면 묶음을 해제하세요)" style={{display:'flex',justifyContent:'center',gap:8,marginTop:3,fontSize:10,lineHeight:1,userSelect:'none'}}>
        <span style={st(canUp)} onClick={()=>canUp && moveWithinBundle(idx,-1)}>▲</span>
        <span style={st(canDown)} onClick={()=>canDown && moveWithinBundle(idx,1)}>▼</span>
      </div>
    );
  };

  // 세부사항 편집 저장
  const saveDetailEdit = (idx, specs, features) => {
    setItems(items.map((it,i)=>i===idx?{...it, _editSpecs:specs, _editFeatures:features, specs, features}:it));
    setEditingDetail(null);
  };

  // 세부사양 (detail_sections) 편집 저장
  const saveSectionsEdit = (idx, sections) => {
    const cleaned = (sections || []).filter(s => (s.title||'').trim() || (s.content||'').trim());
    setItems(items.map((it,i)=>i===idx?{...it, detail_sections:cleaned}:it));
    setEditingDetail(null);
  };

  // 하드웨어사양(BOM) 변경 저장
  const saveHwSpec = (idx, customBom) => {
    const newCost = customBom.reduce((s, b) => s + (parseInt(b.purchase_price)||0) * (parseInt(b.quantity)||1), 0);
    setItems(items.map((it, i) => i === idx ? {
      ...it,
      hw_specs: { ...it.hw_specs, custom_bom: customBom },
      cost_per_unit: newCost
    } : it));
    setEditingHwIdx(null);
  };

  // API 전송용 - pure 함수 (인자로 items 받음). 자동저장 stale closure 방지.
  const cleanItemsOf = (its) => its.map((it, ci) => {
    // 묶음 행: 텍스트·빈값을 display_overrides 에 보존, 숫자 필드는 0/null 로 저장
    const displayOverrides = {};
    let numericUP = pNS(it.unit_price);
    let numericCP = it.consumer_price != null && it.consumer_price !== '' ? parseInt(it.consumer_price) || null : null;
    let numericQTY = parseInt(it.quantity) || 1;
    if (it.bundle && it.item_type !== 'composite') {
      if (!isBundleNum(it.unit_price)) {
        displayOverrides.up = String(it.unit_price ?? '');
        numericUP = 0;
      }
      const cpStr = String(it.consumer_price ?? '');
      if (it.consumer_price !== null && !isBundleNum(it.consumer_price)) {
        displayOverrides.cp = cpStr;
        numericCP = null;
      }
      if (!isBundleNum(it.quantity)) {
        displayOverrides.qty = String(it.quantity ?? '');
        numericQTY = 0;
      }
    }
    const hide = Object.fromEntries(Object.entries(it._hide || {}).filter(([, v]) => v));
    if (Object.keys(hide).length > 0) displayOverrides.hide = hide;
    let costPerUnit = parseInt(it.cost_per_unit)||0;
    if (it.item_type === 'composite') {
      numericUP = compositeUnit(ci, its);
      costPerUnit = compositeCostPerUnit(ci, its);
    }
    if (it._qtyUnset) displayOverrides.qty_unset = true; // 수량 입력 강제 상품구분인데 아직 수량을 입력하지 않음 (임시저장 허용, 발행 차단)
    return {
      item_type:it.item_type, product_id:it.product_id||null, part_id:it.part_id||null,
      item_name:it.item_name, item_model:it.item_model, main_title:it.main_title||'', sub_description:it.sub_description||'', sw_name:it.sw_name||'',
      specs:it._editSpecs||it.specs||[], features:it._editFeatures||it.features||[],
      detail_sections: Array.isArray(it.detail_sections) ? it.detail_sections : [],
      hw_specs:it.hw_specs||{}, description:it.description||'',
      consumer_price: numericCP,
      unit_price: numericUP, quantity: numericQTY,
      cost_per_unit:costPerUnit,
      needs_approval:false, approval_reason:'',
      bundle:it.bundle||null,
      bundle_ref_id:it.bundle_ref_id||null,
      appliance_ref_id:it.appliance_ref_id||null,
      show_in_pdf: it.show_in_pdf !== false,
      display_overrides: Object.keys(displayOverrides).length > 0 ? displayOverrides : {},
    };
  });
  const cleanItems = () => cleanItemsOf(items);

  // 저장
  const showToast = (msg) => { setToast(msg); setTimeout(()=>setToast(null), 2500); };
  const [lastSaved, setLastSaved] = useState(null);
  const [isDirty, setIsDirty] = useState(false);
  const [, setAutoSaveError] = useState(null);
  // 비작성자는 항상 읽기전용 (부서 계층으로 열람한 다른 사람 견적). admin 도 본인 견적이 아니면 RO.
  const isMine = !editId || authorId == null || authorId === authUser?.id;
  const isPublishedSt = estStatus==='published' || estStatus==='unapproved_published';
  const canEditPublished = isPublishedSt && isMine && !!editWindow?.editable && editRemain > 0; // 발행 후 1시간 이내 수정 가능
  const ro = (isPublishedSt && !editing) || estStatus==='pending_approval' || (!!editId && !isMine);

  // 수정 가능 시간 카운트다운 (1초). 0 이 되면 수정 모드를 닫고 새로고침해 읽기전용으로 되돌림
  useEffect(() => {
    if (!editWindow?.editable) return undefined;
    const t = setInterval(() => setEditRemain(r => Math.max(0, r - 1)), 1000);
    return () => clearInterval(t);
  }, [editWindow]);
  useEffect(() => {
    if (editing && editRemain === 0) { alert(`수정 가능 시간(발행 후 ${editWindow?.window_minutes ?? 60}분)이 지났습니다. 저장하지 않은 변경 내용은 사라집니다.\n계속 수정하려면 후속 버전을 생성하세요.`); window.location.reload(); }
  }, [editing, editRemain]);
  const fmtRemain = (sec) => `${String(Math.floor(sec / 60)).padStart(2, '0')}:${String(sec % 60).padStart(2, '0')}`;

  // 변경 감지 (초기 로드 제외)
  const loadedRef = useRef(false);
  useEffect(() => {
    if (!loadedRef.current) { loadedRef.current = true; return; }
    if (!ro) setIsDirty(true);
  }, [info, items, showCP, showCPA, vatSep, vatInputMode, techFeeRate, salesFeeRate]);

  // 묶음 메타 캐시 자동 fetch — 견적 로드/insertBundleById 후 unique bundle_ref_id 의 이름 조회
  useEffect(() => {
    const ids = [...new Set(items.map(it => it.bundle_ref_id).filter(Boolean))];
    const missing = ids.filter(id => !bundleMeta[id]);
    if (missing.length === 0) return;
    missing.forEach(id => {
      api.get(`/bundles/${id}`).then(b => {
        setBundleMeta(prev => ({ ...prev, [id]: { name: b.name } }));
      }).catch(()=>{});
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  // Appliance 메타 캐시 자동 fetch — 견적 로드 후 unique appliance_ref_id 들의 이름 조회
  useEffect(() => {
    const ids = [...new Set(items.map(it => it.appliance_ref_id).filter(Boolean))];
    const missing = ids.filter(id => !applianceMeta[id]);
    if (missing.length === 0) return;
    missing.forEach(id => {
      api.get(`/products/${id}`).then(p => {
        setApplianceMeta(prev => ({ ...prev, [id]: { name: p.name } }));
      }).catch(()=>{});
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  // 새 estId 가 만들어진 시점에 URL 을 ?id=NEW 로 교체하여 새로고침 시 자연 복귀
  const syncUrlToId = (newId) => {
    if (typeof window === 'undefined' || !newId) return;
    const url = new URL(window.location.href);
    url.searchParams.set('id', newId);
    url.searchParams.delete('from');
    window.history.replaceState({}, '', url.toString());
  };

  // 자동저장 (30초) - 매 렌더마다 ref 동기화 + cleanItemsOf(s.items) 로 항상 최신 items 사용
  const autoSaveRef = useRef(null);
  autoSaveRef.current = { info, items, showCP, showCPA, vatSep, vatInputMode, estId, fromId, isDirty, ro, editing, techFeeRate, salesFeeRate };
  useEffect(() => {
    const timer = setInterval(async () => {
      const s = autoSaveRef.current;
      if (s.ro || s.editing || !s.isDirty || s.items.length === 0) return; // 발행 후 수정 중에는 자동저장하지 않음 (수동 '수정 저장'만)
      try {
        const body = {...s.info, show_consumer_price:s.showCP, show_consumer_amount:s.showCPA, vat_separate:s.vatSep, vat_input_mode:s.vatInputMode, tech_fee_rate:s.techFeeRate, sales_fee_rate:s.salesFeeRate, items:cleanItemsOf(s.items)};
        if (s.estId) {
          await api.put(`/estimates/${s.estId}`, body);
        } else if (s.fromId) {
          // 후속버전 첫 자동저장: next-version 으로 새 draft 생성 후 PUT
          const r = await api.post(`/estimates/${s.fromId}/next-version`, {});
          await api.put(`/estimates/${r.id}`, body);
          setEstId(r.id); setDraftId(r.draft_id); setEstStatus('draft');
          syncUrlToId(r.id);
        } else {
          const r = await api.post('/estimates', body);
          setEstId(r.id); setDraftId(r.draft_id); setEstStatus('draft');
          syncUrlToId(r.id);
        }
        setIsDirty(false); setLastSaved(new Date()); setAutoSaveError(null);
        showToast('자동저장됨');
      } catch(e) {
        // 실패 시 사용자에게 알림 (조용히 묻히지 않도록)
        const msg = e?.message || '자동저장 실패';
        setAutoSaveError(msg);
        setToast(`⚠️ 자동저장 실패: ${msg}`);
        setTimeout(() => setToast(null), 4000);
      }
    }, 30000);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 브라우저 닫기 경고
  useEffect(() => {
    const handler = (e) => { if (isDirty && !ro) { e.preventDefault(); e.returnValue = ''; } };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty, ro]);

  // 발행 후 수정 저장: 새 버전 없이 같은 견적서를 갱신 (서버가 1시간·승인정책을 다시 검증)
  const doEditSave = async () => {
    if (publishErrors.length>0) { setTouched({estimate_date:true,valid_until:true,end_user:true,project_name:true,receiver_company:true,receiver_name:true,warranty_terms:true}); alert('저장할 수 없습니다. 다음 항목을 입력해 주세요:\n\n• '+publishErrors.join('\n• ')); return; }
    setSaving(true);
    try {
      const body = {...info, show_consumer_price:showCP, show_consumer_amount:showCPA, vat_separate:vatSep, vat_input_mode:vatInputMode, tech_fee_rate:techFeeRate, sales_fee_rate:salesFeeRate, items:cleanItems()};
      await api.put(`/estimates/${estId}`, body);
      setIsDirty(false);
      showToast('수정되었습니다.');
      setTimeout(() => window.location.reload(), 600);
    } catch(e) { alert(e.message); setSaving(false); }
  };
  const cancelEdit = () => {
    if (isDirty && !window.confirm('수정 중인 내용이 사라집니다. 취소할까요?')) return;
    setIsDirty(false);
    window.location.reload();
  };

  const doDelete = async (pw) => {
    if (estStatus !== 'draft' && !pw) { setDelErr('비밀번호를 입력하세요.'); return; }
    try {
      await api.del(`/estimates/${estId}`, estStatus === 'draft' ? {} : { password: pw });
      navigate('/product-estimate');
    } catch (e) { if (estStatus === 'draft') alert(e.message); else setDelErr(e.message); }
  };
  const doSave = async () => {
    if (saveErrors.length>0) { alert('임시저장할 수 없습니다.\n\n'+saveErrors.join('\n')); return; }
    setSaving(true);
    try {
      const body = {...info, show_consumer_price:showCP, show_consumer_amount:showCPA, vat_separate:vatSep, vat_input_mode:vatInputMode, tech_fee_rate:techFeeRate, sales_fee_rate:salesFeeRate, items:cleanItems()};
      if (fromId && !estId) { const r=await api.post(`/estimates/${fromId}/next-version`,{}); await api.put(`/estimates/${r.id}`,body); setEstId(r.id); setDraftId(r.draft_id); setEstStatus('draft'); syncUrlToId(r.id); showToast('후속버전이 임시저장되었습니다.'); }
      else if (estId) { await api.put(`/estimates/${estId}`,body); showToast('임시저장되었습니다.'); }
      else { const r=await api.post('/estimates',body); setEstId(r.id); setDraftId(r.draft_id); setEstStatus('draft'); syncUrlToId(r.id); showToast(copyId ? '인용 사본이 임시저장되었습니다.' : '임시저장되었습니다.'); }
      setIsDirty(false); setLastSaved(new Date());
    } catch(e) { alert(e.message); } finally { setSaving(false); }
  };

  // 발행
  const doPublish = async () => {
    if (publishErrors.length>0) { setTouched({estimate_date:true,valid_until:true,end_user:true,project_name:true,receiver_company:true,receiver_name:true,warranty_terms:true}); alert('발행할 수 없습니다. 다음 항목을 입력해 주세요:\n\n• '+publishErrors.join('\n• ')); return; }
    setPublishing(true);
    try {
      const body = {...info, show_consumer_price:showCP, show_consumer_amount:showCPA, vat_separate:vatSep, vat_input_mode:vatInputMode, tech_fee_rate:techFeeRate, sales_fee_rate:salesFeeRate, items:cleanItems()};
      let cid=estId;
      if (!cid && fromId) {
        // 후속버전: 먼저 next-version으로 생성
        const r=await api.post(`/estimates/${fromId}/next-version`,{});
        await api.put(`/estimates/${r.id}`,body);
        cid=r.id; setEstId(r.id); syncUrlToId(r.id);
      } else if (!cid) {
        const r=await api.post('/estimates',body); cid=r.id; setEstId(r.id); syncUrlToId(r.id);
      } else {
        await api.put(`/estimates/${cid}`,body);
      }
      const r=await api.post(`/estimates/${cid}/publish`,{approval_reason:approvalReason||undefined});
      if (r.status==='pending_approval') alert('승인 요청이 전송되었습니다.');
      else alert(`발행 완료! 견적번호: ${r.estimate_number}`);
      navigate('/product-estimate');
    } catch(e) {
      if (e.message.includes('승인 사유')) setShowApproval(true);
      else alert(e.message);
    } finally { setPublishing(false); }
  };

  // 새 승인 정책: 항목별 평가 없이 발행 시점에 backend 가 조직 체인 + 견적 공헌이익율로 판단.
  // 작성 화면에서는 견적 전체 공헌이익율을 보여주는 것으로 충분.

  // ro는 위에서 이미 정의됨

  const canPick = !ro;

  // 묶음 id → 펼쳐서 추가. bundle_ref_id 부여로 원자 단위 식별.
  const insertBundleById = async (bundleId) => {
    const b = await api.get(`/bundles/${bundleId}`);
    if (!b || !Array.isArray(b.items)) return;
    const refId = b.id;
    const label = `B${refId}`;
    setBundleMeta(prev => ({ ...prev, [refId]: { name: b.name } }));

    const restored = [];
    for (const it of b.items) {
      const item = {
        ...it,
        unit_price: String(convPrice(parseInt(it.unit_price)||0)),
        consumer_price: convPriceNullable(it.consumer_price),
        bundle: label,
        bundle_ref_id: refId,
        _bundle_name: b.name,
        _editSpecs: it.specs || [],
        _editFeatures: it.features || [],
      };
      if (it.item_type === 'product' && it.product_id) {
        try { item._product = await api.get(`/products/${it.product_id}`); } catch (e) { item._product = null; }
      }
      restored.push(item);
    }
    setItems(prev => [...prev, ...restored]);
    showToast(`공식 묶음 "${b.name}" (${restored.length}개 항목) 추가됨`);
  };

  // ?bundle=id 쿼리 → 새 견적 작성 모드일 때 1회 자동 추가
  const autoBundleAppliedRef = useRef(false);
  useEffect(() => {
    if (autoBundleAppliedRef.current) return;
    if (editId || seedId) return;
    if (!initialBundleId) return;
    autoBundleAppliedRef.current = true;
    insertBundleById(parseInt(initialBundleId, 10)).catch(e => showToast('묶음 자동 추가 실패: ' + (e.message || '')));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialBundleId, editId, seedId]);

  return (
    <div>
      {toast && <div style={{position:'fixed',top:20,left:'50%',transform:'translateX(-50%)',zIndex:9999,background:'#1e293b',color:'#fff',padding:'10px 24px',borderRadius:8,fontSize:13,fontWeight:600,boxShadow:'0 4px 12px rgba(0,0,0,0.15)',animation:'fadeIn .2s'}}>{toast}</div>}
      {canPick && (
        <ItemPickerModal
          open={showItemPicker}
          onClose={() => setShowItemPicker(false)}
          products={products}
          parts={allParts}
          favorites={favorites}
          onToggleFavorite={toggleFavorite}
          onAddProduct={addProduct}
          onAddPart={addPart}
          onInsertBundleById={insertBundleById}
        />
      )}
      {/* 1. 상단바 */}
      <div className="card" style={{padding:'12px 18px',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
        <div style={{display:'flex',alignItems:'center',gap:12,fontSize:13,color:'#64748b'}}>
          <span className={`badge ${sBadge[estStatus]||'badge-gray'}`}>{sLabel[estStatus]||'신규'}</span>
          {(estNumber || draftId) && <span>{estNumber || draftId}</span>}
          <VersionTabs versions={versions} currentId={editId} basePath="/product-estimate/new" />
          {lastSaved && <span style={{fontSize:11,color:'#94a3b8'}}>마지막 저장 {lastSaved.toLocaleTimeString('ko-KR',{hour:'2-digit',minute:'2-digit',second:'2-digit'})}</span>}
          {fromId && <span className="badge badge-blue">후속버전 작성 중</span>}
          {editing && <span className="badge badge-orange">발행 후 수정 중 · {fmtRemain(editRemain)} 남음</span>}
        </div>
        <div className="btn-group">
          <button className="btn btn-outline" onClick={()=>navigate('/product-estimate')}>목록으로</button>
          {estId && isMine && <button className="btn btn-outline" onClick={()=>setShowShareModal(true)}>👥 공유</button>}
          {ro && estId && estStatus==='pending_approval' && <button className="btn btn-outline" onClick={()=>window.open(`/estimates/print?id=${estId}&preview=1`,'_blank')}>👁 미리보기</button>}
          {ro && estId && estStatus!=='pending_approval' && <button className="btn btn-outline" onClick={()=>window.open(`/estimates/print?id=${estId}`,'_blank')}>🖨 PDF 저장</button>}
          {ro && canEditPublished && <button className="btn btn-outline" style={{borderColor:'#f59e0b',color:'#b45309'}} title={`새 버전을 만들지 않고 이 견적서를 직접 수정합니다. 발행 후 ${editWindow?.window_minutes ?? 60}분 이내에만 가능합니다.`} onClick={()=>setEditing(true)}>✏ 수정 ({fmtRemain(editRemain)} 남음)</button>}
          {ro && estId && isMine && (estStatus==='published' || estStatus==='unapproved_published') && <button className="btn btn-primary" onClick={()=>{ window.location.href = `/product-estimate/new?from=${estId}`; }}>📑 후속버전</button>}
          {estId && isMine && !editing && (estStatus==='draft' || canPerformAction('제품 견적서 삭제')) && <button className="btn" style={{background:'#fef2f2',color:'#dc2626',border:'1px solid #fecaca'}} onClick={()=>{ if (estStatus==='draft') { if (window.confirm('이 견적서를 삭제하시겠습니까?')) doDelete(''); } else { setDelPw(''); setDelErr(''); setDelModal(true); } }}>삭제</button>}
          {editing && <button className="btn btn-outline" onClick={cancelEdit} disabled={saving}>수정 취소</button>}
          {editing && <button className="btn btn-primary" onClick={doEditSave} disabled={saving}>{saving?'저장 중...':'💾 수정 저장'}</button>}
          {ro && estId && !isMine && <button className="btn btn-primary" onClick={()=>{ window.location.href = `/product-estimate/new?copy=${estId}`; }}>📋 인용해 새 견적 작성</button>}
          {!ro && !editing && canEdit && <button className="btn btn-outline" onClick={doSave} disabled={saving}>{saving?'저장 중...':'💾 임시저장'}</button>}
          {!ro && !editing && <button className="btn btn-outline" onClick={async()=>{
            let cid=estId;
            if(!cid){
              if(items.length===0){alert('견적 항목을 1개 이상 추가해 주세요.');return;}
              setSaving(true);
              try{
                const body={...info,show_consumer_price:showCP,vat_separate:vatSep,vat_input_mode:vatInputMode,tech_fee_rate:techFeeRate,sales_fee_rate:salesFeeRate,items:cleanItems()};
                if(fromId){const r=await api.post(`/estimates/${fromId}/next-version`,{});await api.put(`/estimates/${r.id}`,body);cid=r.id;setEstId(r.id);setDraftId(r.draft_id);setEstStatus('draft');syncUrlToId(r.id);}
                else{const r=await api.post('/estimates',body);cid=r.id;setEstId(r.id);setDraftId(r.draft_id);setEstStatus('draft');syncUrlToId(r.id);}
              }catch(e){alert(e.message);setSaving(false);return;}finally{setSaving(false);}
            } else {
              try{await api.put(`/estimates/${cid}`,{...info,show_consumer_price:showCP,vat_separate:vatSep,vat_input_mode:vatInputMode,tech_fee_rate:techFeeRate,sales_fee_rate:salesFeeRate,items:cleanItems()});}catch(e){alert(e.message);return;}
            }
            window.open(`/estimates/print?id=${cid}&preview=1`,'_blank');
          }}>👁 미리보기</button>}
          {!ro && !editing && canEdit && <button className="btn btn-primary" onClick={()=>{setShowApproval(false);doPublish();}} disabled={publishing} style={publishErrors.length>0?{opacity:0.7}:{}}>{publishing?'발행 중...':'📤 발행'}</button>}
        </div>
      </div>

      <OldVersionBanner versions={versions} currentId={editId} basePath="/product-estimate/new" />

      {/* 2. 기본정보 */}
      <div className="card">
        <div className="card-title" style={{marginBottom:12}}>기본 정보</div>
        <div className="form-row">
          <div className="form-group" style={{flex:1}}><label className="form-label">견적일자 *</label><input type="date" className="form-input" style={fieldErr('estimate_date',info.estimate_date)} value={info.estimate_date} onChange={e=>setInfo({...info,estimate_date:e.target.value})} onBlur={()=>markTouched('estimate_date')} disabled={ro} />{fieldHint('estimate_date',info.estimate_date,'견적일자')}</div>
          <div className="form-group" style={{flex:1.5}}>
            <label className="form-label">유효기간 *</label>
            <div style={{display:'flex',gap:8,alignItems:'center'}}>
              <input type="date" className="form-input" value={info.valid_until} onChange={e=>setInfo({...info,valid_until:e.target.value})} onBlur={()=>markTouched('valid_until')} disabled={ro} style={{flex:1,...fieldErr('valid_until',info.valid_until)}} />
              {!ro && <><button className="btn btn-outline btn-sm" onClick={()=>setValid(7)}>1주</button><button className="btn btn-outline btn-sm" onClick={()=>setValid(14)}>2주</button><button className="btn btn-outline btn-sm" onClick={()=>setValid(30)}>1개월</button></>}
            </div>
            {fieldHint('valid_until',info.valid_until,'유효기간')}
          </div>
          <div className="form-group" style={{flex:1}}>
            <label className="form-label">견적서 양식</label>
            <select className="form-input" value={info.template_id} onChange={e=>{const tpl=templates.find(t=>String(t.id)===e.target.value);if(tpl)applyTemplate(tpl);else setInfo({...info,template_id:e.target.value});}} disabled={ro}>
              {templates.length === 0 && <option value="">템플릿 없음</option>}
              {templates.map(t=><option key={t.id} value={t.id}>{t.name}{t.is_default?' (기본)':''}</option>)}
            </select>
          </div>
        </div>
        <div className="form-row">
          <div className="form-group" style={{flex:1}}><label className="form-label">최종사용자 *</label><input className="form-input" style={fieldErr('end_user',info.end_user)} value={info.end_user} onChange={e=>setInfo({...info,end_user:e.target.value})} onBlur={()=>markTouched('end_user')} disabled={ro} />{fieldHint('end_user',info.end_user,'최종사용자')}</div>
          <div className="form-group" style={{flex:2}}><label className="form-label">사업명 *</label><input className="form-input" style={fieldErr('project_name',info.project_name)} value={info.project_name} onChange={e=>setInfo({...info,project_name:e.target.value})} onBlur={()=>markTouched('project_name')} disabled={ro} />{fieldHint('project_name',info.project_name,'사업명')}</div>
          <div className="form-group" style={{flex:'0 0 140px'}}><label className="form-label">태그</label>
            <select className="form-input" value={tagId} onChange={e=>changeTag(e.target.value)} disabled={!isMine}>
              <option value="">-</option>{tagList.map(t=><option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>
        </div>
      </div>

      {/* 3. 수신자 + 결제 */}
      <div className="grid-2">
        <div className="card">
          <div className="card-title" style={{marginBottom:12}}>견적 수신자</div>
          <div className="form-group" style={{position:'relative'}}>
            <label className="form-label">수신처 * (회사명)</label>
            <div style={{display:'flex',gap:8}}>
              <input className="form-input" style={{flex:1,...fieldErr('receiver_company',info.receiver_company)}} value={info.receiver_company}
                onChange={e=>{setInfo({...info,receiver_company:e.target.value});setSelectedCustomer(null);}} onBlur={()=>markTouched('receiver_company')} disabled={ro} placeholder="수신처를 입력하세요" />
              <button className="btn btn-outline btn-sm" onClick={()=>{setShowReceiverSearch(true);setReceiverSearchQuery('');setReceiverSearchResults([]);}} disabled={ro}>🔍 검색</button>
            </div>
            {fieldHint('receiver_company',info.receiver_company,'수신처')}
            {/* 유사 수신처 자동추천 */}
            {receiverSuggestions.length > 0 && (
              <div style={{background:'#fffbeb',border:'1px solid #fde68a',borderRadius:7,padding:'7px 11px',fontSize:12,marginTop:5,color:'#92400e'}}>
                유사한 등록 수신처: {receiverSuggestions.map((s,i)=>(
                  <span key={s.id}>{i>0 && ', '}<span style={{color:'#3b82f6',cursor:'pointer',fontWeight:600}} onClick={()=>selectCustomer(s)}>{s.name}</span></span>
                ))}
              </div>
            )}
          </div>
          <div className="form-group" style={{marginBottom:0}}>
            <label className="form-label">수신자 * (담당자)</label>
            <input className="form-input" style={fieldErr('receiver_name',info.receiver_name)} value={info.receiver_name}
              onChange={e=>setInfo({...info,receiver_name:e.target.value})} onBlur={()=>markTouched('receiver_name')} disabled={ro} placeholder="담당자명" />
            {fieldHint('receiver_name',info.receiver_name,'수신자')}
            <div className="text-muted" style={{marginTop:4}}>등록된 수신처 선택 시 소속 담당자가 자동 입력됩니다</div>
          </div>
          {/* 신규 수신처 안내 */}
          {!ro && info.receiver_company && !selectedCustomer && (
            <div className="alert alert-info" style={{marginTop:10,marginBottom:0}}>ℹ 새 수신처/수신자인 경우 발행 후 고객관리에 자동 등록됩니다. (세부정보 입력 필요)</div>
          )}
        </div>
        <div className="card">
          <div className="card-title" style={{marginBottom:12}}>결제 / 납기 / 무상하자보증</div>
          <div className="form-group"><label className="form-label">결제조건</label><input className="form-input" value={info.payment_terms} onChange={e=>setInfo({...info,payment_terms:e.target.value})} disabled={ro} /></div>
          <div className="form-group"><label className="form-label">납기</label><input className="form-input" value={info.delivery_terms} onChange={e=>setInfo({...info,delivery_terms:e.target.value})} disabled={ro} /></div>
          <div className="form-group" style={{marginBottom:0}}><label className="form-label">무상하자보증기간 *</label><input className="form-input" style={fieldErr('warranty_terms',info.warranty_terms)} value={info.warranty_terms} onChange={e=>setInfo({...info,warranty_terms:e.target.value})} onBlur={()=>markTouched('warranty_terms')} disabled={ro} />{fieldHint('warranty_terms',info.warranty_terms,'무상하자보증기간')}</div>
        </div>
      </div>

      {/* 4. 견적항목 */}
      <div className="card estimate-items-drop-zone" style={{padding:16}}
        onContextMenu={(e) => {
          if (ro) return;
          e.preventDefault();
          setSnippetCtxMenu({ x: e.clientX, y: e.clientY });
        }}
        {...(!ro ? {
          onDragOver: (e) => {
            const t = e.dataTransfer.types || [];
            if (t.includes && t.includes('application/x-soosan-item')) {
              e.preventDefault(); e.dataTransfer.dropEffect = 'copy';
              e.currentTarget.classList.add('drag-over');
            }
          },
          onDragLeave: (e) => e.currentTarget.classList.remove('drag-over'),
          onDrop: async (e) => {
            e.currentTarget.classList.remove('drag-over');
            try {
              const raw = e.dataTransfer.getData('application/x-soosan-item');
              if (!raw) return;
              e.preventDefault();
              const payload = JSON.parse(raw);
              const { kind, item, bundleId } = payload;
              if (kind === 'product') addProduct(item);
              else if (kind === 'part') addPart(item);
              else if (kind === 'bundle' && bundleId) {
                try { await insertBundleById(bundleId); }
                catch (err) { showToast('묶음 불러오기 실패: ' + (err.message || '오류')); }
              }
            } catch (err) {}
          },
        } : {})}>
        <div className="card-header" style={{marginBottom:10}}>
          <div className="card-title">견적 항목 {!ro && <span style={{fontSize:11,color:'#94a3b8',fontWeight:400,marginLeft:8}}>← 끌어다 놓기 / 우클릭으로 묶음 저장</span>}</div>
          {!ro && <div className="btn-group">
            <button className="btn btn-outline btn-sm" onClick={()=>setShowItemPicker(true)} title="저장된 묶음 / 즐겨찾기 / 전체 제품·부품">⭐ 자주 쓰는 항목</button>
            <button className="btn btn-outline btn-sm" onClick={()=>{setShowAddProd(true);setAddSearch('');setAddType('all');}}>⚙ 상품 추가</button>
            <button className="btn btn-outline btn-sm" onClick={()=>{setShowAddPart(true);setAddSearch('');}}>▧ 부품 추가</button>
            <button className="btn btn-outline btn-sm" onClick={()=>setItems([...items,{item_type:'manual',item_name:'',item_model:'',description:'',consumer_price:null,unit_price:'0',quantity:1,cost_per_unit:0,bundle:'',show_in_pdf:true}])}>✎ 수기입력 항목</button>
            <button className="btn btn-outline btn-sm" title="바로 위쪽의 같은 묶음 번호 행들을 합산하는 소계 행을 추가합니다. 합산 공급가액 × 수량이 공급금액이 됩니다." onClick={()=>setItems([...items,{item_type:'composite',item_name:'소계',item_model:'',description:'',consumer_price:null,unit_price:'0',quantity:'',cost_per_unit:0,bundle:'',show_in_pdf:true,_qtyUnset:true,_qtyForced:true}])}>∑ 소계 행</button>
          </div>}
        </div>

        <div style={{display:'flex',alignItems:'center',gap:22,marginBottom:14,padding:'8px 12px',background:'#f8fafc',borderRadius:8}}>
          <label style={{display:'flex',alignItems:'center',gap:8,fontSize:12,color:'#64748b',cursor:'pointer'}}>소비자단가 <div className={`toggle ${showCP?'on':''}`} onClick={()=>{if(showCP)setShowCPA(false);setShowCP(!showCP);}}><div className="toggle-knob"></div></div></label>
          <label style={{display:'flex',alignItems:'center',gap:8,fontSize:12,color:showCP?'#64748b':'#cbd5e1',cursor:showCP?'pointer':'default'}}>소비자금액 <div className={`toggle ${showCPA&&showCP?'on':''}`} onClick={()=>{if(showCP)setShowCPA(!showCPA);}} style={!showCP?{opacity:0.4,pointerEvents:'none'}:{}}><div className="toggle-knob"></div></div></label>
          <label style={{display:'flex',alignItems:'center',gap:8,fontSize:12,color:'#64748b',cursor:'pointer'}}>부가세 별도 <div className={`toggle ${vatSep?'on':''}`} onClick={()=>{
            // vatSep 토글: 현재 unit_price/consumer_price 해석(별도/포함)이 바뀌면 환산
            const newVatSep = !vatSep;
            const wasInclusive = !vatSep && vatInputMode === 'inclusive';
            const willBeInclusive = !newVatSep && vatInputMode === 'inclusive';
            if (wasInclusive !== willBeInclusive) {
              const conv = (v) => willBeInclusive ? Math.round(Number(v||0)*1.1) : Math.round(Number(v||0)/1.1);
              setItems(items.map(it => ({
                ...it,
                unit_price: String(conv(pNS(it.unit_price))),
                consumer_price: it.consumer_price != null ? conv(it.consumer_price) : null,
              })));
            }
            if (newVatSep) setVatInputMode('separate'); // vatSep ON 이면 입력방식은 의미 없음
            setVatSep(newVatSep);
          }}><div className="toggle-knob"></div></div></label>
          {!vatSep && !ro && (
            <div style={{display:'flex',alignItems:'center',gap:12,fontSize:12,color:'#64748b'}}>
              <span style={{color:'#94a3b8'}}>입력방식:</span>
              <label style={{display:'flex',alignItems:'center',gap:4,cursor:'pointer'}}>
                <input type="radio" name="vatInputMode" checked={vatInputMode==='separate'} onChange={()=>{
                  if (vatInputMode === 'inclusive') {
                    // inclusive → separate: 기존 unit_price/consumer_price 는 포함값이었음 → /1.1
                    setItems(items.map(it => ({
                      ...it,
                      unit_price: String(Math.round(pNS(it.unit_price)/1.1)),
                      consumer_price: it.consumer_price != null ? Math.round(Number(it.consumer_price)/1.1) : null,
                    })));
                  }
                  setVatInputMode('separate');
                }} />
                별도 금액 입력
              </label>
              <label style={{display:'flex',alignItems:'center',gap:4,cursor:'pointer'}}>
                <input type="radio" name="vatInputMode" checked={vatInputMode==='inclusive'} onChange={()=>{
                  if (vatInputMode !== 'inclusive') {
                    // separate → inclusive: 기존 unit_price/consumer_price 는 별도값이었음 → ×1.1
                    setItems(items.map(it => ({
                      ...it,
                      unit_price: String(Math.round(pNS(it.unit_price)*1.1)),
                      consumer_price: it.consumer_price != null ? Math.round(Number(it.consumer_price)*1.1) : null,
                    })));
                  }
                  setVatInputMode('inclusive');
                }} />
                포함 금액 입력
              </label>
            </div>
          )}
        </div>

        <div style={{overflowX:'auto'}}>
          <table style={{fontSize:12,width:'100%',borderCollapse:'collapse'}}>
            <thead><tr style={{background:'#f8fafc'}}>
              {!ro && <th style={{padding:'9px 10px',textAlign:'center',width:50,fontWeight:600,fontSize:11,color:'#64748b',borderBottom:'2px solid #e2e8f0'}}>묶음</th>}
              <th style={{padding:'9px 10px',textAlign:'left',minWidth:130,fontWeight:600,fontSize:11,color:'#64748b',borderBottom:'2px solid #e2e8f0'}}>견적항목</th>
              <th style={{padding:'9px 10px',textAlign:'left',minWidth:260,fontWeight:600,fontSize:11,color:'#64748b',borderBottom:'2px solid #e2e8f0'}}>세부사항</th>
              {showCP && <th style={{padding:'9px 10px',textAlign:'right',minWidth:100,fontWeight:600,fontSize:11,color:'#64748b',borderBottom:'2px solid #e2e8f0'}}>소비자단가</th>}
              {showCP && showCPA && <th style={{padding:'9px 10px',textAlign:'right',minWidth:100,fontWeight:600,fontSize:11,color:'#64748b',borderBottom:'2px solid #e2e8f0'}}>소비자금액</th>}
              <th style={{padding:'9px 10px',textAlign:'right',minWidth:130,fontWeight:600,fontSize:11,color:'#64748b',borderBottom:'2px solid #e2e8f0'}}>공급가액</th>
              <th colSpan={useSplitQty?2:1} style={{padding:'9px 10px',textAlign:'center',minWidth:60,fontWeight:600,fontSize:11,color:'#64748b',borderBottom:'2px solid #e2e8f0'}}>수량</th>
              <th style={{padding:'9px 10px',textAlign:'right',minWidth:120,fontWeight:600,fontSize:11,color:'#64748b',borderBottom:'2px solid #e2e8f0'}}>공급금액</th>
              <th style={{padding:'9px 10px',textAlign:'right',minWidth:110,fontWeight:600,fontSize:11,color:'#64748b',borderBottom:'2px solid #e2e8f0'}}>원가합계</th>
              <th style={{padding:'9px 10px',textAlign:'center',minWidth:70,fontWeight:600,fontSize:11,color:'#64748b',borderBottom:'2px solid #e2e8f0'}}>마진율</th>
              {!ro && <th style={{width:30,borderBottom:'2px solid #e2e8f0'}}></th>}
              {!ro && <th style={{width:26,borderBottom:'2px solid #e2e8f0'}}></th>}
            </tr></thead>
            <tbody>
              {(() => {
                // 묶음 그룹 계산
                const bundleMap = {};
                items.forEach((it, i) => {
                  if (!it.bundle) return;
                  if (!bundleMap[it.bundle]) bundleMap[it.bundle] = [];
                  bundleMap[it.bundle].push(i);
                });
                // 묶음의 병합 셀(견적항목·마진율·정렬 화살표)이 덮는 행들. 소계 행도 같은 묶음 번호면 그 묶음의 한 행이다.
                const groupOf = (idx) => { const b = items[idx].bundle; return b && bundleMap[b]?.length > 1 ? bundleMap[b] : null; };
                // 소계 행이 있으면 구성 행들의 공급금액 칸을 소계 행까지 하나로 병합 (소계 × 수량만 표시)
                const mergeAmt = {}, skipAmt = new Set(), memberOf = {};
                items.forEach((c, ci) => {
                  if (c.item_type !== 'composite') return;
                  const ms = compositeMembersIdx(items, ci);
                  if (ms.length === 0) return;
                  mergeAmt[ms[0]] = { span: ms.length + 1, ci };
                  ms.forEach(j => { memberOf[j] = ci; });
                  ms.slice(1).forEach(j => skipAmt.add(j));
                  skipAmt.add(ci);
                });
                const groupMgOf = (idxs) => {
                  const gct = idxs.reduce((a, gi) => a + costOf(items[gi], gi), 0);
                  const glt = idxs.reduce((a, gi) => {
                    const row = items[gi];
                    if (row.item_type === 'composite' || memberSet.has(gi)) return a + supplyOf(row, gi);
                    if (!isBundleNum(row.unit_price) || !isBundleNum(row.quantity)) return a;
                    return a + pNS(row.unit_price) * parseInt(row.quantity);
                  }, 0);
                  const lt = isInclusiveStored ? Math.round(glt/1.1) : glt;
                  return lt > 0 ? (((lt-gct)/lt)*100).toFixed(1) : '-';
                };
                // 소계 행: 세부사항 칸에 소계 이름, 공급가액=구성 행 합계, 수량 입력, 공급금액은 병합 칸에 표시
                const renderComposite = (it, idx) => {
                  const unit = compositeUnit(idx);
                  const qty = parseInt(it.quantity) || 1;
                  const nMembers = compositeMembersIdx(items, idx).length;
                  const bGroup = groupOf(idx);
                  const showItemCell = !bGroup || bGroup[0] === idx;
                  const mg = bGroup ? groupMgOf(bGroup) : groupMgOf([idx]);
                  const lt = costOf(it, idx);
                  const cpCols = (showCP?1:0) + (showCP&&showCPA?1:0);
                  const th = {padding:10,background:'#eef2f7'};
                  const span = bGroup ? bGroup.length : 1;
                  return (
                    <tr key={idx} style={{borderBottom:'1px solid #f1f5f9',verticalAlign:'top'}}>
                      {!ro && <td style={{padding:'8px 4px',textAlign:'center'}}>
                        <input style={{width:36,padding:'3px 0',border:'1px solid #e2e8f0',borderRadius:5,fontSize:12,textAlign:'center',outline:'none',background:bGroup?'#eff6ff':'transparent'}} value={it.bundle||''} onChange={e=>upItem(idx,'bundle',e.target.value)} />
                        {rowArrows(idx)}
                      </td>}
                      {showItemCell && <td style={{padding:10}} rowSpan={span}></td>}
                      <td style={th}>
                        {ro
                          ? <div style={{fontWeight:700,fontSize:13,textAlign:'right',whiteSpace:'pre-wrap'}}>{it.item_name}</div>
                          : <textarea style={{width:'100%',padding:'5px 8px',border:'1px solid #d1d5db',borderRadius:6,fontSize:12,resize:'vertical',minHeight:36,fontWeight:700,textAlign:'right',...(!it.item_name?{borderColor:'#ef4444',background:'#fef2f2'}:{})}} rows={1} value={it.item_name||''} onChange={e=>upItem(idx,'item_name',e.target.value)} placeholder="소계 *" />}
                        <div style={{marginTop:4,textAlign:'right',fontSize:10,color:nMembers?'#64748b':'#ef4444'}}><span className="badge badge-purple">소계</span> {nMembers ? `바로 위 ${nMembers}개 행 합산` : '합산할 행이 없습니다 (바로 위에 같은 묶음 번호의 행이 있어야 합니다)'}</div>
                      </td>
                      {cpCols > 0 && <td colSpan={cpCols} style={th}></td>}
                      <td colSpan={nMembers>0?2:1} style={{...th,textAlign:'right',fontWeight:700}}>₩{dispPrice(unit).toLocaleString()}</td>
                      {nMembers===0 && <td colSpan={useSplitQty?2:1} style={{...th,textAlign:'center'}}>{ro?fmtNumericDisplay(it.quantity):<SmartNumInput placeholder="수량" title="소계에 곱할 수량 (필수)" style={{width:50,padding:5,border:`1px solid ${it._qtyUnset?'#ef4444':'#d1d5db'}`,background:it._qtyUnset?'#fef2f2':'#fff',borderRadius:6,fontSize:13,textAlign:'center',outline:'none'}} value={it.quantity} onValueChange={v=>onQtyChange(idx,v)} />}</td>}
                      {!skipAmt.has(idx) && <td style={{...th,textAlign:'right',fontWeight:700,fontSize:14}}>₩{(dispPrice(unit)*qty).toLocaleString()}</td>}
                      <td style={{...th,textAlign:'right'}}>₩{lt.toLocaleString()}</td>
                      {showItemCell && <td style={{padding:10,textAlign:'center',fontWeight:700,color:parseFloat(mg)>30?'#22c55e':parseFloat(mg)>0?'#f59e0b':'#ef4444'}} rowSpan={span}>{mg}{mg!=='-'?'%':''}</td>}
                      {!ro && showItemCell && (() => {
                        const gFirst = bGroup ? bGroup[0] : idx;
                        const gLast  = bGroup ? bGroup[bGroup.length-1] : idx;
                        const canUp = gFirst > 0, canDown = gLast < items.length-1;
                        return (
                          <td style={{padding:'6px 2px',textAlign:'center',verticalAlign:'middle'}} rowSpan={span}>
                            <div style={{display:'flex',flexDirection:'column',gap:2,fontSize:11,lineHeight:1,userSelect:'none'}}>
                              <span title="위로" style={{cursor:canUp?'pointer':'default',opacity:canUp?0.7:0.2,color:'#64748b'}} onClick={()=>canUp && moveItem(idx,-1)}>▲</span>
                              <span title="아래로" style={{cursor:canDown?'pointer':'default',opacity:canDown?0.7:0.2,color:'#64748b'}} onClick={()=>canDown && moveItem(idx,1)}>▼</span>
                            </div>
                          </td>
                        );
                      })()}
                      {!ro && <td style={{padding:10}}><span style={{color:'#ef4444',cursor:'pointer',fontSize:15,opacity:0.5}} onClick={()=>removeItem(idx)}>✕</span></td>}
                    </tr>
                  );
                };
                return items.map((it,idx) => {
                if (it.item_type === 'composite') return renderComposite(it, idx);
                const ltRaw = pNS(it.unit_price)*(parseInt(it.quantity)||1);
                // margin 계산은 항상 별도(exclusive) 기준
                const lt = isInclusiveStored ? Math.round(ltRaw/1.1) : ltRaw;
                const ct = (parseInt(it.cost_per_unit)||0)*(parseInt(it.quantity)||1);
                const mg = lt>0?(((lt-ct)/lt)*100).toFixed(1):'-';
                const prod = it._product;
                const hwParts = it.hw_specs?.custom_bom ? it.hw_specs.custom_bom.filter(b=>b.show_in_spec) : prod?.bom?.filter(b=>b.show_in_spec)||[];
                const specs = it._editSpecs || it.specs || [];
                const features = it._editFeatures || it.features || [];
                const isEditing = editingDetail?.idx === idx;

                const bGroup = groupOf(idx);
                const isFirst = bGroup ? bGroup[0] === idx : true;
                const showItemCell = !bGroup || isFirst;
                // 묶음: 각 행 독립 기여. 그룹 마진 = 전체 행의 합으로 계산 (소계 행이 있으면 구성 행 대신 소계 행 기준)
                const groupMg = bGroup ? groupMgOf(bGroup) : mg;

                const isHidden = it.show_in_pdf === false;
                return (
                  <tr key={idx} style={{borderBottom:'1px solid #f1f5f9',verticalAlign:'top',...(isHidden?{background:'#f8fafc',opacity:0.85}:{})}}>
                    {!ro && <td style={{padding:'8px 4px',textAlign:'center'}}>
                      {it.bundle_ref_id ? (() => {
                        const bName = it._bundle_name || bundleMeta[it.bundle_ref_id]?.name || `묶음 #${it.bundle_ref_id}`;
                        return (
                          <span title={`묶음 (분리 불가) — ${bName}`}
                            style={{display:'inline-block',padding:'3px 6px',borderRadius:5,
                              background:'#e0e7ff',color:'#4338ca',fontSize:11,fontWeight:700}}>
                            🔒{it.bundle}
                          </span>
                        );
                      })() : it.appliance_ref_id ? (() => {
                        const aName = it._appliance_name || applianceMeta[it.appliance_ref_id]?.name || `Appliance #${it.appliance_ref_id}`;
                        return (
                          <span title={`Appliance 묶음 (분리 불가) — ${aName}`}
                            style={{display:'inline-block',padding:'3px 6px',borderRadius:5,
                              background:'#dcfce7',color:'#15803d',fontSize:11,fontWeight:700}}>
                            🔒{it.bundle}
                          </span>
                        );
                      })() : (
                        <input style={{width:36,padding:'3px 0',border:'1px solid #e2e8f0',borderRadius:5,fontSize:12,textAlign:'center',outline:'none',background:bGroup?'#eff6ff':'transparent'}} value={it.bundle||''} onChange={e=>upItem(idx,'bundle',e.target.value)} placeholder="" />
                      )}
                      {rowArrows(idx)}
                    </td>}
                    {showItemCell && <td style={{padding:10}} rowSpan={bGroup ? bGroup.length : 1}>
                      {it.item_type==='manual' && !ro ? (
                        <><textarea style={{width:'100%',padding:'5px 8px',border:'1px solid #d1d5db',borderRadius:6,fontSize:12,resize:'vertical',minHeight:36,...(!it.item_name?{borderColor:'#ef4444',background:'#fef2f2'}:{})}} rows={2} value={it.item_name} onChange={e=>upItem(idx,'item_name',e.target.value)} placeholder="견적항목명 *" />
                          <div style={{marginTop:4,display:'flex',alignItems:'center',gap:6,flexWrap:'wrap'}}>
                            <span className="badge badge-purple">수기</span>
                            <button type="button" onClick={()=>upItem(idx,'show_in_pdf',it.show_in_pdf===false)}
                              title="견적서 PDF 본문에 포함 여부. OFF 면 소비자단가/공급가액 입력 비활성 + PDF 미포함, 원가는 공헌이익 계산에 그대로 반영."
                              style={{padding:'2px 8px',fontSize:10,borderRadius:4,fontWeight:700,cursor:'pointer',
                                border:`1px solid ${it.show_in_pdf!==false?'#3b82f6':'#94a3b8'}`,
                                background:it.show_in_pdf!==false?'#eff6ff':'#f1f5f9',
                                color:it.show_in_pdf!==false?'#1e40af':'#64748b'}}>
                              📄 PDF {it.show_in_pdf!==false?'포함':'제외'}
                            </button>
                          </div>
                        </>
                      ) : (
                        <>
                          {ro
                            ? <div style={{fontWeight:700,fontSize:13,whiteSpace:'pre-wrap'}}>{it.item_model||it.item_name}</div>
                            : <textarea style={{width:'100%',padding:'5px 8px',border:'1px solid #d1d5db',borderRadius:6,fontSize:12,resize:'vertical',minHeight:36,fontWeight:700}} rows={2} value={it.item_model||it.item_name||''} onChange={e=>{
                                // 상품은 item_model, 부품은 item_name 수정
                                upItem(idx, it.item_type==='product' ? 'item_model' : 'item_name', e.target.value);
                              }} placeholder="견적항목명" />
                          }
                          <div style={{marginTop:4,display:'flex',alignItems:'center',gap:6,flexWrap:'wrap'}}>
                            <span className={`badge ${it.item_type==='product'?'badge-blue':it.item_type==='manual'?'badge-purple':'badge-gray'}`}>{it.item_type==='product'?'상품':it.item_type==='manual'?'수기':'부품'}</span>
                          </div>
                        </>
                      )}
                    </td>}

                    <td style={{padding:10,fontSize:11,lineHeight:1.8}}>
                      {it.item_type==='manual' ? (
                        !ro ? <textarea style={{width:'100%',padding:'5px 8px',border:'1px solid #d1d5db',borderRadius:6,fontSize:11,resize:'vertical',minHeight:48,...(!it.description?{borderColor:'#ef4444',background:'#fef2f2'}:{})}} rows={3} value={it.description||''} onChange={e=>upItem(idx,'description',e.target.value)} placeholder="세부사항 *" />
                        : <div style={{color:'#475569',whiteSpace:'pre-wrap'}}>{it.description}</div>
                      ) : it.item_type==='product' && (prod || prod === null || !it.product_id) ? ( // 상품이 삭제돼 연결이 없어도 견적 항목에 저장된 세부사항을 그대로 표시
                        <>
                          <div style={{marginBottom:6}}>
                            <span style={{fontSize:10,fontWeight:700,color:'#94a3b8'}}>세부사양 {!ro && <span style={{color:'#3b82f6',cursor:'pointer',fontSize:10}} onClick={()=>setEditingDetail({idx,section:'sections'})}>[수정]</span>}</span>
                            {isEditing && editingDetail.section==='sections' ? (
                              <SectionsEditor sections={deriveDetailSections(it)} onSave={(s)=>saveSectionsEdit(idx,s)} onCancel={()=>setEditingDetail(null)} />
                            ) : deriveDetailSections(it).map((s,i)=>(
                              <div key={`sec-${i}`} style={{marginBottom:4}}>
                                {s.title && <div style={{fontWeight:700,fontSize:12}}>{s.title}</div>}
                                {s.content && <div style={{fontSize:11,whiteSpace:'pre-wrap'}}>{s.content}</div>}
                              </div>
                            ))}
                          </div>
                          {specs.length>0 && (
                            <div style={{marginBottom:6}}>
                              <span style={{fontSize:10,fontWeight:700,color:'#94a3b8'}}>성능 {!ro && <span style={{color:'#3b82f6',cursor:'pointer',fontSize:10}} onClick={()=>setEditingDetail({idx,section:'specs'})}>[수정]</span>}</span>
                              {isEditing && editingDetail.section==='specs' ? (
                                <SpecEditor specs={specs} features={features} onSave={(s,f)=>saveDetailEdit(idx,s,f)} onCancel={()=>setEditingDetail(null)} section="specs" />
                              ) : specs.map((s,i)=><div key={i}><span style={{color:'#64748b',display:'inline-block',width:80}}>{s.key}</span>{s.value}</div>)}
                            </div>
                          )}
                          {hwParts.length>0 && (
                            <div style={{marginBottom:6}}>
                              <span style={{fontSize:10,fontWeight:700,color:'#94a3b8'}}>하드웨어사양 {!ro && <span style={{color:'#3b82f6',cursor:'pointer',fontSize:10}} onClick={()=>setEditingHwIdx(idx)}>[수정]</span>}</span>
                              {hwParts.map((b,i)=><div key={i}>· {b.part_type||b.part_name}: {b.part_spec_display||b.part_spec}{(b.quantity||1)>1?` x${b.quantity}${b.part_unit||''}`:''}</div>)}
                            </div>
                          )}
                        </>
                      ) : it.item_type==='product' ? <span style={{color:'#94a3b8'}}>상품 상세 로딩 중...</span>
                        : <div style={{color:'#475569'}}>{it.description||it.item_name}</div>}
                    </td>

                    {showCP && <td style={{padding:10,textAlign:'right',color:'#94a3b8'}}>
                      {hideBtn(it,idx,'cp')}
                      {ro
                        ? (() => {
                            if (it.bundle && !isBundleNum(it.consumer_price)) return it.consumer_price || '-';
                            return it.consumer_price ? `₩${dispPrice(Number(it.consumer_price)).toLocaleString()}` : '-';
                          })()
                        : it.bundle
                          ? <SmartNumInput disabled={isHidden} style={{width:100,padding:'5px 9px',border:'1px solid #d1d5db',borderRadius:6,fontSize:12,textAlign:'right',outline:'none',...(isHidden?{background:'#e2e8f0',cursor:'not-allowed'}:{})}} value={it.consumer_price ?? ''} onValueChange={v=>upItem(idx,'consumer_price',v)} placeholder={isHidden?'PDF 제외':'선택'} />
                          : <input disabled={isHidden} style={{width:100,padding:'5px 9px',border:'1px solid #d1d5db',borderRadius:6,fontSize:12,textAlign:'right',outline:'none',...(isHidden?{background:'#e2e8f0',cursor:'not-allowed'}:{})}} value={it.consumer_price?fmtN(it.consumer_price):''} onChange={e=>upItem(idx,'consumer_price',pN(e.target.value)||null)} placeholder={isHidden?'PDF 제외':'선택'} />}
                    </td>}
                    {showCP && showCPA && <td style={{padding:10,textAlign:'right',color:'#94a3b8'}}>{it.consumer_price && isBundleNum(it.consumer_price)?`₩${(dispPrice(Number(it.consumer_price))*(parseInt(it.quantity)||1)).toLocaleString()}`:'-'}</td>}

                    {/* 공급가액: 묶음일 때도 rowSpan 없이 각 행마다 렌더 — 자식 행은 입력칸 없이 가격 hint 만 표시 */}
                    <td style={{padding:10,textAlign:'right',position:'relative'}}>
                      <div style={{display:'flex',justifyContent:prod?'space-between':'flex-end',gap:4,alignItems:'flex-start',width:120,marginLeft:'auto'}}>
                        {prod && <button type="button" onClick={e=>{e.stopPropagation(); setGuideIdx(idx);}}
                          onMouseEnter={()=>setGuideIdx(idx)} onMouseLeave={()=>setGuideIdx(null)}
                          style={{display:'block',marginBottom:3,padding:'0 5px',fontSize:9,lineHeight:'15px',borderRadius:3,cursor:'pointer',fontWeight:700,border:'1px solid #e2e8f0',background:'#f8fafc',color:'#94a3b8'}}>가이드금액</button>}
                        {hideBtn(it,idx,'up')}
                      </div>
                      {prod && guideIdx===idx && (
                        <div onClick={e=>e.stopPropagation()} style={{position:'absolute',right:10,top:28,zIndex:30,background:'#1e293b',color:'#fff',fontSize:11,lineHeight:1.8,padding:'8px 12px',borderRadius:6,boxShadow:'0 4px 12px rgba(0,0,0,0.25)',whiteSpace:'nowrap',textAlign:'left',fontWeight:400}}>
                          <div>총판(Distributor) ₩{Number(prod.price_distributor||0).toLocaleString()}</div>
                          <div>협력사(Partner) ₩{Number(prod.price_partner||0).toLocaleString()}</div>
                          <div>최종사용자(EU) ₩{Number(prod.price_end_user||0).toLocaleString()}</div>
                        </div>
                      )}
                      {showItemCell ? (
                        <>
                          {ro
                            ? (() => {
                                if (it.bundle && !isBundleNum(it.unit_price)) return <span style={{fontWeight:700}}>{it.unit_price || ''}</span>;
                                return <span style={{fontWeight:700}}>₩{dispPrice(pNS(it.unit_price)).toLocaleString()}</span>;
                              })()
                            : (() => {
                              // 묶음 행: 자유 텍스트 허용
                              if (it.bundle) {
                                return <SmartNumInput disabled={isHidden} style={{width:120,padding:'5px 9px',border:'1px solid #d1d5db',borderRadius:6,fontSize:13,textAlign:'right',outline:'none',color:'#222',...(isHidden?{background:'#e2e8f0',cursor:'not-allowed'}:{})}}
                                  value={it.unit_price ?? ''}
                                  onValueChange={v=>upItem(idx,'unit_price',v)}
                                  placeholder={isHidden?'PDF 제외':'단가 or 텍스트'}
                                  title="숫자 입력 시 합산 적용, 텍스트 입력 시 그대로 표시, 비워두면 빈칸" />;
                              }
                              const stored = pNS(it.unit_price);
                              const isInclusiveMode = !vatSep && vatInputMode === 'inclusive';
                              const exclusiveValue = isInclusiveMode ? Math.round(stored / 1.1) : stored;
                              const inclusiveValue = isInclusiveMode ? stored : Math.round(stored * 1.1);
                              return <>
                                {!vatSep && <div style={{fontWeight:700,fontSize:13}}>₩{inclusiveValue.toLocaleString()}</div>}
                                {(() => {
                                  const allowNeg = it.item_type === 'manual';
                                  // 음수 허용 행: state 에 raw('-', '-1000') 보존 → '-' 만 타이핑해도 입력칸에 표시됨
                                  // 음수 불가 행: pN 으로 숫자만 강제 추출 → '-' 입력 즉시 제거
                                  const handleChange = allowNeg
                                    ? (e) => {
                                        const cleaned = e.target.value.replace(/[^0-9-]/g, '');
                                        const neg = cleaned.startsWith('-');
                                        const digits = cleaned.replace(/-/g, '');
                                        upItem(idx, 'unit_price', (neg ? '-' : '') + digits);
                                      }
                                    : (e) => upItem(idx, 'unit_price', String(pN(e.target.value)));
                                  // 음수 허용 행은 state 의 raw 문자열을 표시용으로 포맷, 불가 행은 fmtN 으로 양수만
                                  const displayValue = allowNeg
                                    ? (() => {
                                        const s = String(it.unit_price ?? '');
                                        const neg = s.startsWith('-');
                                        const digits = s.replace(/[^0-9]/g, '');
                                        if (!digits) return neg ? '-' : '';
                                        return (neg ? '-' : '') + Number(digits).toLocaleString();
                                      })()
                                    : fmtN(stored);
                                  return <input disabled={isHidden} style={{width:120,padding:'5px 9px',border:'1px solid #d1d5db',borderRadius:6,fontSize:vatSep?13:10,textAlign:'right',outline:'none',color:vatSep?'#222':'#94a3b8',...(isHidden?{background:'#e2e8f0',cursor:'not-allowed'}:{})}}
                                    value={displayValue}
                                    onChange={handleChange}
                                    title={(isInclusiveMode ? 'VAT 포함 단가 입력' : 'VAT 미포함 단가 입력') + (allowNeg ? ' (음수 가능)' : '')} />;
                                })()}
                                {!vatSep && (
                                  <div style={{fontSize:10,color:'#94a3b8',marginTop:2}}>
                                    {isInclusiveMode ? `별도 ₩${exclusiveValue.toLocaleString()}` : `포함 ₩${inclusiveValue.toLocaleString()}`}
                                  </div>
                                )}
                              </>;
                            })()
                          }
                        </>
                      ) : (
                        // 묶음 자식 행: 부모와 동일한 방식으로 공급가액 입력
                        <>
                          {ro
                            ? (() => {
                                if (!isBundleNum(it.unit_price)) return <span style={{fontWeight:700}}>{it.unit_price || ''}</span>;
                                return <span style={{fontWeight:700}}>₩{dispPrice(pNS(it.unit_price)).toLocaleString()}</span>;
                              })()
                            : <input disabled={isHidden} style={{width:120,padding:'5px 9px',border:'1px solid #d1d5db',borderRadius:6,fontSize:13,textAlign:'right',outline:'none',color:'#222',...(isHidden?{background:'#e2e8f0',cursor:'not-allowed'}:{})}}
                                value={it.unit_price ?? ''}
                                onChange={e=>upItem(idx,'unit_price',e.target.value)}
                                placeholder="단가 or 텍스트" />}
                        </>
                      )}
                    </td>

                    {/* 수량: 묶음 자식도 본인 qty 입력 가능 (per-row) */}
                    <td colSpan={useSplitQty && memberOf[idx]===undefined ? 2 : 1} style={{padding:10,textAlign:'center'}}>{hideBtn(it,idx,'qty')}{ro?fmtNumericDisplay(it.quantity):<SmartNumInput placeholder={it._qtyUnset?'수량':''} title={it._qtyUnset?'이 상품구분은 수량을 직접 입력해야 발행할 수 있습니다':''} style={{width:50,padding:5,border:`1px solid ${it._qtyUnset?'#ef4444':'#d1d5db'}`,background:it._qtyUnset?'#fef2f2':'#fff',borderRadius:6,fontSize:13,textAlign:'center',outline:'none'}} value={it.quantity} onValueChange={v=>onQtyChange(idx,v)} />}</td>
                    {mergeAmt[idx] && (() => { const c = items[mergeAmt[idx].ci]; return (
                      <td rowSpan={mergeAmt[idx].span} style={{padding:10,textAlign:'center',verticalAlign:'bottom'}}>
                        {ro?fmtNumericDisplay(c.quantity):<SmartNumInput placeholder="수량" title="소계에 곱할 수량 (필수)" style={{width:50,padding:5,border:`1px solid ${c._qtyUnset?'#ef4444':'#d1d5db'}`,background:c._qtyUnset?'#fef2f2':'#fff',borderRadius:6,fontSize:13,textAlign:'center',outline:'none'}} value={c.quantity} onValueChange={v=>onQtyChange(mergeAmt[idx].ci,v)} />}
                      </td>
                    ); })()}
                    {mergeAmt[idx] ? (
                      <td rowSpan={mergeAmt[idx].span} style={{padding:10,textAlign:'right',fontWeight:700,fontSize:14,verticalAlign:'bottom'}}>
                        ₩{(dispPrice(compositeUnit(mergeAmt[idx].ci))*(parseInt(items[mergeAmt[idx].ci].quantity)||1)).toLocaleString()}
                      </td>
                    ) : !skipAmt.has(idx) && (
                    <td style={{padding:10,textAlign:'right',fontWeight:700,fontSize:14}}>
                      {hideBtn(it,idx,'amt')}
                      {it.bundle
                        ? (() => {
                            if (!isBundleNum(it.unit_price)) return <span style={{color:'#64748b',fontWeight:400}}>{it.unit_price || ''}</span>;
                            if (!isBundleNum(it.quantity)) return '';
                            return `₩${(dispPrice(pNS(it.unit_price))*parseInt(it.quantity)).toLocaleString()}`;
                          })()
                        : `₩${(dispPrice(pNS(it.unit_price))*(parseInt(it.quantity)||1)).toLocaleString()}`}
                    </td>
                    )}
                    {/* 원가합계: 묶음/단품 모두 본인 cost × own_qty (per-row). 수기항목은 cost 직접 입력. */}
                    <td style={{padding:10,textAlign:'right',position:'relative'}}>
                      {it.item_type === 'manual' && !ro ? (
                        <div style={{display:'flex',alignItems:'center',gap:4,justifyContent:'flex-end'}}>
                          <input style={{width:90,padding:'4px 6px',border:'1px solid #d1d5db',borderRadius:5,fontSize:11,textAlign:'right',outline:'none'}}
                            value={it.cost_per_unit?Number(it.cost_per_unit).toLocaleString():''}
                            onChange={e=>upItem(idx,'cost_per_unit',pN(e.target.value)||0)}
                            placeholder="원가" />
                          <span style={{fontSize:10,color:'#94a3b8'}}>×{parseInt(it.quantity)||1}</span>
                          <span style={{fontSize:11,fontWeight:700,minWidth:60}}>₩{ct.toLocaleString()}</span>
                        </div>
                      ) : (() => {
                        const effBom = (it.hw_specs?.custom_bom?.length ? it.hw_specs.custom_bom : prod?.bom) || [];
                        const showBomTip = it.item_type === 'product' && effBom.length > 0;
                        return (
                          <span className="cost-tooltip-wrap">
                            <span className="cost-value-hover">₩{ct.toLocaleString()}</span>
                            {showBomTip && (
                              <div className="cost-tip">
                                <div style={{fontWeight:700,marginBottom:6}}>{it.item_name} BOM 원가 (단품)</div>
                                <table style={{width:'100%'}}><tbody>
                                  {effBom.filter(b => (Number(b.purchase_price)||0) * (Number(b.quantity)||0) > 0).map((b,i) => (
                                    <tr key={i}>
                                      <td style={{border:'none',padding:'2px 24px 2px 0',textAlign:'left'}}>{b.part_name}</td>
                                      <td style={{border:'none',padding:'2px 0',textAlign:'right',fontWeight:600,whiteSpace:'nowrap'}}>₩{Number(b.purchase_price||0).toLocaleString()} x{b.quantity}</td>
                                    </tr>
                                  ))}
                                </tbody></table>
                                <div style={{borderTop:'1px solid #e2e8f0',paddingTop:4,marginTop:4}}>단품: ₩{Number(it.cost_per_unit||0).toLocaleString()} x{it.quantity} = <strong>₩{ct.toLocaleString()}</strong></div>
                              </div>
                            )}
                          </span>
                        );
                      })()}
                    </td>
                    {showItemCell && <td style={{padding:10,textAlign:'center',fontWeight:700,color:parseFloat(groupMg)>30?'#22c55e':parseFloat(groupMg)>0?'#f59e0b':'#ef4444'}} rowSpan={bGroup ? bGroup.length : 1}>{groupMg}%<div style={{fontSize:10,fontWeight:400}}>{parseFloat(groupMg)>30?'✓':'⚠'}</div></td>}
                    {!ro && showItemCell && (() => {
                      const gFirst = bGroup ? bGroup[0] : idx;
                      const gLast  = bGroup ? bGroup[bGroup.length-1] : idx;
                      const canUp = gFirst > 0;
                      const canDown = gLast < items.length-1;
                      return (
                        <td style={{padding:'6px 2px',textAlign:'center',verticalAlign:'middle'}} rowSpan={bGroup ? bGroup.length : 1}>
                          <div style={{display:'flex',flexDirection:'column',gap:2,fontSize:11,lineHeight:1,userSelect:'none'}}>
                            <span title="위로" style={{cursor:canUp?'pointer':'default',opacity:canUp?0.7:0.2,color:'#64748b'}} onClick={()=>canUp && moveItem(idx,-1)}>▲</span>
                            <span title="아래로" style={{cursor:canDown?'pointer':'default',opacity:canDown?0.7:0.2,color:'#64748b'}} onClick={()=>canDown && moveItem(idx,1)}>▼</span>
                          </div>
                        </td>
                      );
                    })()}
                    {!ro && <td style={{padding:10}}><span style={{color:'#ef4444',cursor:'pointer',fontSize:15,opacity:0.5}} onClick={()=>removeItem(idx)}>✕</span></td>}
                  </tr>
                );
              }); })()}
              {items.length===0 && <tr><td colSpan={ro?(7+(showCP?1:0)+(showCPA&&showCP?1:0)):(10+(showCP?1:0)+(showCPA&&showCP?1:0))} style={{textAlign:'center',padding:32,color:'#94a3b8'}}>상품 또는 부품을 추가하세요.</td></tr>}

              {items.length>0 && (()=>{const cs=(useSplitQty?1:0)+(ro?(4+(showCP?1:0)+(showCPA&&showCP?1:0)):(5+(showCP?1:0)+(showCPA&&showCP?1:0)));const tail=ro?2:4;return(<>
                {vatSep ? (<>
                  <tr style={{borderTop:'2px solid #1e293b'}}><td colSpan={cs} style={{textAlign:'right',fontWeight:700,padding:10,background:'#f8fafc'}}>합계금액</td><td style={{textAlign:'right',fontWeight:800,fontSize:15,padding:10,background:'#f8fafc'}}>₩{subtotal.toLocaleString()}</td><td colSpan={tail} style={{background:'#f8fafc'}}></td></tr>
                  <tr><td colSpan={cs} style={{textAlign:'right',color:'#64748b',padding:'8px 10px',background:'#f8fafc'}}>부가가치세</td><td style={{textAlign:'right',color:'#64748b',padding:'8px 10px',background:'#f8fafc'}}>₩{vat.toLocaleString()}</td><td colSpan={tail} style={{background:'#f8fafc'}}></td></tr>
                  <tr><td colSpan={cs} style={{textAlign:'right',fontWeight:800,padding:10,background:'#eff6ff'}}>최종견적가(VAT포함)</td><td style={{textAlign:'right',fontWeight:800,fontSize:17,color:'#3b82f6',padding:10,background:'#eff6ff'}}>₩{total.toLocaleString()}</td><td colSpan={tail} style={{background:'#eff6ff'}}></td></tr>
                </>) : (
                  <tr style={{borderTop:'2px solid #1e293b'}}><td colSpan={cs} style={{textAlign:'right',fontWeight:800,padding:10,background:'#eff6ff'}}>최종견적가(VAT포함)</td><td style={{textAlign:'right',fontWeight:800,fontSize:17,color:'#3b82f6',padding:10,background:'#eff6ff'}}>₩{dispSubtotal.toLocaleString()}</td><td colSpan={tail} style={{background:'#eff6ff'}}></td></tr>
                )}

                {/* 공헌이익 시뮬레이션 */}
                <tr><td colSpan={cs+1+tail} style={{padding:'14px 10px 4px 10px',fontSize:11,fontWeight:700,color:'#3b82f6',borderTop:'2px dashed #cbd5e1'}}>💡 공헌이익 시뮬레이션</td></tr>
                <tr><td colSpan={cs} style={{textAlign:'right',color:'#64748b',padding:'6px 10px'}}>항목 원가</td><td style={{textAlign:'right',padding:'6px 10px'}}>₩{itemCostTotal.toLocaleString()}</td><td colSpan={tail}></td></tr>
                <tr><td colSpan={cs} style={{textAlign:'right',color:'#64748b',padding:'6px 10px'}}>기술수수료&nbsp;
                  <input type="number" min="0" step="0.1" value={techFeeRate}
                    onChange={e=>setTechFeeRate(parseFloat(e.target.value)||0)}
                    disabled={ro}
                    style={{width:64,padding:'3px 6px',textAlign:'right',border:'1px solid #d1d5db',borderRadius:5,fontSize:12,outline:'none'}} />&nbsp;%</td>
                  <td style={{textAlign:'right',padding:'6px 10px'}}>₩{techFee.toLocaleString()}</td><td colSpan={tail}></td></tr>
                <tr><td colSpan={cs} style={{textAlign:'right',color:'#64748b',padding:'6px 10px'}}>영업수수료&nbsp;
                  <input type="number" min="0" step="0.1" value={salesFeeRate}
                    onChange={e=>setSalesFeeRate(parseFloat(e.target.value)||0)}
                    disabled={ro}
                    style={{width:64,padding:'3px 6px',textAlign:'right',border:'1px solid #d1d5db',borderRadius:5,fontSize:12,outline:'none'}} />&nbsp;%</td>
                  <td style={{textAlign:'right',padding:'6px 10px'}}>₩{salesFee.toLocaleString()}</td><td colSpan={tail}></td></tr>
                <tr><td colSpan={cs} style={{textAlign:'right',fontWeight:700,padding:'8px 10px',background:'#f8fafc'}}>원가합계</td><td style={{textAlign:'right',fontWeight:700,padding:'8px 10px',background:'#f8fafc'}}>₩{costTotalWithFees.toLocaleString()}</td><td colSpan={tail} style={{background:'#f8fafc'}}></td></tr>
                <tr><td colSpan={cs} style={{textAlign:'right',fontWeight:800,padding:'8px 10px',background:'#ecfdf5'}}>공헌이익</td><td style={{textAlign:'right',fontWeight:800,fontSize:15,color:contribution>=0?'#059669':'#ef4444',padding:'8px 10px',background:'#ecfdf5'}}>₩{contribution.toLocaleString()}</td><td colSpan={tail} style={{background:'#ecfdf5'}}></td></tr>
                <tr><td colSpan={cs} style={{textAlign:'right',fontWeight:700,padding:'8px 10px',background:'#ecfdf5'}}>공헌이익율</td><td style={{textAlign:'right',fontWeight:700,color:contributionRate>=0?'#059669':'#ef4444',padding:'8px 10px',background:'#ecfdf5'}}>{contributionRate.toFixed(1)} %</td><td colSpan={tail} style={{background:'#ecfdf5'}}></td></tr>
                {(() => {
                  const failing = approvalChain.filter(c => c.min_margin_rate != null && contributionRate < c.min_margin_rate);
                  if (approvalChain.length === 0) return null;
                  if (failing.length === 0) {
                    return <tr><td colSpan={cs+1+tail} style={{padding:'10px 12px',background:'#f0fdf4',border:'1px solid #bbf7d0',borderRadius:6,fontSize:12,color:'#166534'}}>✓ 즉시 발행 가능 (모든 조직 임계값 충족)</td></tr>;
                  }
                  return <tr><td colSpan={cs+1+tail} style={{padding:'10px 12px',background:'#fffbeb',border:'1px solid #fde68a',borderRadius:6,fontSize:12,color:'#92400e',lineHeight:1.7}}>
                    ⚠ 발행 시 다음 결재선이 필요합니다 (공헌이익율 {contributionRate.toFixed(1)}% 미달):
                    <div style={{marginTop:4,fontWeight:600}}>
                      {failing.map((c,i) => (
                        <span key={c.leader_uid}>
                          L{i+1} {c.leader_name} <span style={{color:'#a16207',fontWeight:400}}>({c.org_name} {c.min_margin_rate}%)</span>
                          {i < failing.length-1 && <span style={{margin:'0 6px',color:'#a16207'}}>→</span>}
                        </span>
                      ))}
                    </div>
                  </td></tr>;
                })()}
              </>);})()}
            </tbody>
          </table>
        </div>
      </div>

      {/* 5. 승인 알림 */}
      {estStatus==='pending_approval' && approvalInfo ? (
        <div className="alert alert-warning" style={{borderRadius:10,padding:'14px 18px'}}>
          <div style={{flex:1}}>
            <strong style={{fontSize:13}}>⚠ 이 견적서는 승인 대기 중입니다</strong>
            {approvalInfo.reason && <div style={{marginTop:5,fontSize:12,color:'#92400e'}}>요청 사유: {approvalInfo.reason}</div>}
            {approvalInfo.trigger_items?.length > 0 && (
              <div style={{marginTop:5,fontSize:12,lineHeight:1.8}}>
                {approvalInfo.trigger_items.map((t,i)=><div key={i}>· {t.product||t.item_name}: {t.reason}</div>)}
              </div>
            )}
            <div style={{marginTop:8,fontSize:12}}>
              <span style={{color:'#64748b'}}>승인 경로: </span>
              {(approvalInfo.approval_path||[]).map((p,i)=>(
                <span key={i}>
                  <span className={`badge ${p.status==='approved'?'badge-green':p.status==='pending'?'badge-yellow':'badge-gray'}`} style={{fontSize:10}}>{p.approver_name}({p.org_name})</span>
                  {i < approvalInfo.approval_path.length-1 && <span style={{margin:'0 4px',color:'#94a3b8'}}>→</span>}
                </span>
              ))}
            </div>
            {!isAdmin && <div style={{marginTop:12,display:'flex',gap:8,justifyContent:'center'}}>
              <button className="btn btn-primary btn-sm" onClick={()=>{setShowApproveModal(true);setApprovePassword('');}}>✓ 승인</button>
              <button className="btn btn-danger btn-sm" style={{background:'#ef4444',color:'#fff',border:'none'}} onClick={()=>{setShowRejectModal(true);setRejectReasonText('');}}>✕ 반려</button>
            </div>}
            {isAdmin && <div style={{marginTop:8,fontSize:11,color:'#94a3b8',textAlign:'center'}}>시스템 관리자는 열람만 가능합니다.</div>}
          </div>
        </div>
      ) : null}

      {/* 6. 비고 */}
      <div className="card"><div className="card-title" style={{marginBottom:8}}>비고</div><textarea className="form-input" rows={3} value={info.notes} onChange={e=>setInfo({...info,notes:e.target.value})} disabled={ro} placeholder="고객에게 전달할 비고사항..." /></div>

      {/* 7. 내부 메모 */}
      {estId && (
        <div className="card">
          <div className="card-title" style={{marginBottom:12}}>내부 메모</div>
          <div style={{display:'flex',gap:8,marginBottom:12}}>
            <input className="form-input" style={{flex:1}} value={memoText} onChange={e=>setMemoText(e.target.value)} placeholder="메모를 입력하세요..." onKeyDown={e=>{if(e.key==='Enter'&&memoText){api.post(`/estimates/${estId}/memos`,{content:memoText}).then(m=>{setMemos([m,...memos]);setMemoText('');}).catch(err=>alert(err.message));}}} />
            <button className="btn btn-primary btn-sm" disabled={!memoText} onClick={()=>{api.post(`/estimates/${estId}/memos`,{content:memoText}).then(m=>{setMemos([m,...memos]);setMemoText('');}).catch(err=>alert(err.message));}}>등록</button>
          </div>
          {memos.length > 0 ? memos.map(m=>(
            <div key={m.id} style={{display:'flex',gap:10,padding:'8px 0',borderBottom:'1px solid #f1f5f9',alignItems:'flex-start'}}>
              <div className="avatar" style={{width:28,height:28,fontSize:11,flexShrink:0}}>{m.user_name?.[0]}</div>
              <div style={{flex:1}}>
                <div style={{fontSize:12}}><strong>{m.user_name}</strong> <span style={{color:'#94a3b8',fontSize:11}}>{new Date(m.created_at).toLocaleString('ko-KR',{month:'2-digit',day:'2-digit',hour:'2-digit',minute:'2-digit'})}</span></div>
                <div style={{fontSize:13,marginTop:2,whiteSpace:'pre-wrap'}}>{m.content}</div>
              </div>
              {m.user_id===authUser?.id && <span style={{color:'#94a3b8',cursor:'pointer',fontSize:12}} onClick={()=>{api.del(`/estimates/${estId}/memos/${m.id}`).then(()=>setMemos(memos.filter(x=>x.id!==m.id)));}}>삭제</span>}
            </div>
          )) : <div className="text-muted" style={{textAlign:'center',padding:16}}>메모가 없습니다.</div>}
        </div>
      )}

      {/* 8. 버전이력 */}
      {versions.length>0 && (
        <div className="card">
          <div className="card-header"><div className="card-title" style={{marginBottom:0}}>버전 이력</div></div>
          <VersionMemoCompare versions={versions} currentId={editId} canEditNote={!!editId && isMine}
            onNoteSaved={(id,note)=>setVersions(vs=>vs.map(x=>x.id===id?{...x,version_note:note}:x))} />
          <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill, minmax(220px, 1fr))',gap:12}}>
            {versions.map(v=>{
              const isCurrent = v.id === parseInt(editId);
              const names = v.item_names_top || [];
              const count = v.item_count || 0;
              const itemSummary = count === 0 ? '항목 없음'
                : count <= names.length ? names.join(' / ')
                : `${names.join(' / ')} 외 ${count - names.length}건`;
              return (
                <div key={v.id}
                  onClick={()=>{ if(!isCurrent) window.location.assign(`/product-estimate/new?id=${v.id}`); }}
                  style={{
                    border: isCurrent ? '2px solid #3b82f6' : '1px solid #e2e8f0',
                    borderRadius:10, padding:14, background: isCurrent ? '#eff6ff' : '#fff',
                    cursor: isCurrent ? 'default' : 'pointer',
                    transition:'all 0.15s',
                  }}>
                  <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:8}}>
                    <span className={`badge ${isCurrent ? 'badge-blue' : 'badge-gray'}`}>
                      v{v.version}{isCurrent && ' (현재)'}
                    </span>
                    <span style={{fontSize:11,color:'#94a3b8'}}>{v.created_at?.slice(0,16).replace('T',' ')}</span>
                  </div>
                  <div style={{fontSize:12,color:'#64748b',marginBottom:6,minHeight:32,lineHeight:1.4}}>
                    {itemSummary}
                  </div>
                  {v.version_note && <div style={{fontSize:11,color:'#475569',fontStyle:'italic',marginBottom:6}}>“{v.version_note}”</div>}
                  <div style={{fontSize:15,fontWeight:700,color:'#0f172a'}}>
                    ₩ {Number(v.total_amount || 0).toLocaleString()}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* === MODALS === */}

      {/* 상품 추가 */}
      {showAddProd && (<div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.4)',zIndex:100,display:'flex',alignItems:'center',justifyContent:'center'}} onClick={()=>setShowAddProd(false)}><div onClick={e=>e.stopPropagation()} style={{background:'#fff',borderRadius:14,padding:28,width:1000,maxWidth:'92vw',maxHeight:'80vh',overflowY:'auto'}}>
        <div style={{fontSize:16,fontWeight:700,marginBottom:16,paddingBottom:10,borderBottom:'1px solid #e2e8f0',display:'flex',justifyContent:'space-between'}}><span>상품 추가</span><span style={{cursor:'pointer',color:'#94a3b8'}} onClick={()=>setShowAddProd(false)}>✕</span></div>
        <div style={{display:'flex',gap:6,marginBottom:10,flexWrap:'wrap'}}>
          {['all','software','license','hardware','appliance'].map(t=>(
            <button key={t} className={`btn btn-sm ${addType===t?'btn-primary':'btn-outline'}`} onClick={()=>setAddType(t)}>
              {t==='all'?'전체':TYPE_LABEL[t]}
            </button>
          ))}
        </div>
        <div className="form-group"><input className="form-input" value={addSearch} onChange={e=>setAddSearch(e.target.value)} placeholder="상품명, 모델명, 별칭 검색..." /></div>
        <table style={{fontSize:12}}><thead><tr>
          <th style={{width:60}}>유형</th><th>상품구분</th><th>상품명</th><th>모델명</th><th>별칭</th><th style={{textAlign:'right'}}>원가합계</th><th style={{width:60}}></th>
        </tr></thead><tbody>
          {products
            .filter(p=>addType==='all'||p.product_type===addType)
            .filter(p=>matchAnyCI(addSearch, p.name, p.model, p.alias))
            .map(p=>(
            <tr key={p.id}>
              <td><span className={`badge ${TYPE_BADGE[p.product_type]||'badge-gray'}`} title={TYPE_LABEL[p.product_type]||p.product_type}>{TYPE_SHORT[p.product_type]||p.product_type||'-'}</span></td>
              <td>{p.category||'-'}</td>
              <td className="fw-bold">{p.name}</td>
              <td>{p.model}</td>
              <td>{p.alias||'-'}</td>
              <td style={{textAlign:'right'}}>₩{Number(p.total_cost||0).toLocaleString()}</td>
              <td><button className="btn btn-primary btn-sm" onClick={()=>addProduct(p)}>추가</button></td>
            </tr>
          ))}
        </tbody></table></div></div>)}

      {/* 부품 추가 */}
      {showAddPart && (<div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.4)',zIndex:100,display:'flex',alignItems:'center',justifyContent:'center'}} onClick={()=>setShowAddPart(false)}><div onClick={e=>e.stopPropagation()} style={{background:'#fff',borderRadius:14,padding:28,width:600,maxHeight:'80vh',overflowY:'auto'}}>
        <div style={{fontSize:16,fontWeight:700,marginBottom:16,paddingBottom:10,borderBottom:'1px solid #e2e8f0',display:'flex',justifyContent:'space-between'}}><span>부품 추가</span><span style={{cursor:'pointer',color:'#94a3b8'}} onClick={()=>setShowAddPart(false)}>✕</span></div>
        <div className="form-group"><input className="form-input" value={addSearch} onChange={e=>setAddSearch(e.target.value)} placeholder="부품명, 코드 검색..." /></div>
        <table style={{fontSize:12}}><thead><tr><th>코드</th><th>부품명</th><th>규격</th><th style={{textAlign:'right'}}>매입가</th><th></th></tr></thead><tbody>
          {allParts.filter(p=>matchAnyCI(addSearch, p.name, p.code, p.spec)).map(p=>(<tr key={p.id}><td style={{fontFamily:'monospace'}}>{p.code}</td><td>{p.name}</td><td>{p.spec}</td><td style={{textAlign:'right'}}>₩{Number(p.purchase_price||0).toLocaleString()}</td><td><button className="btn btn-primary btn-sm" onClick={()=>addPart(p)}>추가</button></td></tr>))}
        </tbody></table></div></div>)}

      {/* 수신처 검색 모달 */}
      {showReceiverSearch && (<div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.4)',zIndex:100,display:'flex',alignItems:'center',justifyContent:'center'}} onClick={()=>setShowReceiverSearch(false)}><div onClick={e=>e.stopPropagation()} style={{background:'#fff',borderRadius:14,padding:28,width:550,maxHeight:'80vh',overflowY:'auto'}}>
        <div style={{fontSize:16,fontWeight:700,marginBottom:16,paddingBottom:10,borderBottom:'1px solid #e2e8f0',display:'flex',justifyContent:'space-between'}}><span>수신처 검색</span><span style={{cursor:'pointer',color:'#94a3b8'}} onClick={()=>setShowReceiverSearch(false)}>✕</span></div>
        <div style={{display:'flex',gap:8,marginBottom:16}}><input className="form-input" value={receiverSearchQuery} onChange={e=>setReceiverSearchQuery(e.target.value)} placeholder="고객명 검색..." onKeyDown={e=>{if(e.key==='Enter')searchReceiver();}} style={{flex:1}} /><button className="btn btn-primary btn-sm" onClick={searchReceiver}>검색</button></div>
        <table style={{fontSize:12}}><thead><tr><th>회사명</th><th>유형</th><th>담당자</th><th></th></tr></thead><tbody>
          {receiverSearchResults.map(c=>(<tr key={c.id}><td className="fw-bold">{c.name}</td><td>{c.customer_type}</td><td>{c.contact_name}</td><td><button className="btn btn-primary btn-sm" onClick={()=>{selectCustomer(c);setShowReceiverSearch(false);}}>선택</button></td></tr>))}
          {receiverSearchResults.length===0 && <tr><td colSpan={4} style={{textAlign:'center',padding:20,color:'#94a3b8'}}>검색어를 입력하세요.</td></tr>}
        </tbody></table></div></div>)}

      {delModal && (
        <div className="modal-overlay" onClick={()=>setDelModal(false)}>
          <div className="modal" style={{width:400}} onClick={ev=>ev.stopPropagation()}>
            <div className="modal-header"><h3>견적서 삭제</h3><button className="modal-close" onClick={()=>setDelModal(false)}>✕</button></div>
            <div className="modal-body">
              <div className="alert alert-danger" style={{marginBottom:16}}>삭제 예정으로 전환됩니다.<br/>7일 후 완전 삭제되며, 그 전에 복원할 수 있습니다.</div>
              <div className="form-group">
                <label className="form-label">비밀번호 확인</label>
                <input className="form-input" type="password" placeholder="비밀번호를 입력하세요" value={delPw} onChange={ev=>{setDelPw(ev.target.value);setDelErr('');}} onKeyDown={ev=>ev.key==='Enter'&&doDelete(delPw)} autoFocus />
              </div>
              {delErr && <div style={{color:'#dc2626',fontSize:12,marginTop:4}}>{delErr}</div>}
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={()=>setDelModal(false)}>취소</button>
              <button className="btn" style={{background:'#dc2626',color:'#fff'}} onClick={()=>doDelete(delPw)}>삭제</button>
            </div>
          </div>
        </div>
      )}
      {/* 공유 모달 */}
      {showShareModal && (() => {
        const closeShare = () => { setShowShareModal(false); setShareQuery(''); setShareSearch([]); };
        const loadShares = async () => { try { setShareList(await api.get(`/estimates/${estId}/shares`)); } catch(e) { alert(e.message); } };
        const addShare = async (u) => {
          try { await api.post(`/estimates/${estId}/shares`, { user_ids: [u.id] }); setShareQuery(''); setShareSearch([]); await loadShares(); }
          catch (e) { alert(e.message); }
        };
        const removeShare = async (userId) => {
          try { await api.del(`/estimates/${estId}/shares/${userId}`); await loadShares(); }
          catch (e) { alert(e.message); }
        };
        const onSearchChange = (q) => {
          setShareQuery(q);
          if (shareSearchTimerRef.current) clearTimeout(shareSearchTimerRef.current);
          if (q.trim().length < 1) { setShareSearch([]); return; }
          shareSearchTimerRef.current = setTimeout(async () => {
            try {
              const r = await api.get(`/users?search=${encodeURIComponent(q.trim())}`);
              const sharedIds = new Set(shareList.map(s => s.target_user_id));
              setShareSearch(r.filter(u => u.id !== authUser?.id && !sharedIds.has(u.id)).slice(0, 8));
            } catch(e) { setShareSearch([]); }
          }, 250);
        };
        return (
          <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.4)',zIndex:100,display:'flex',alignItems:'center',justifyContent:'center'}} onClick={closeShare}>
            <div onClick={e=>e.stopPropagation()} style={{background:'#fff',borderRadius:14,padding:24,width:480,maxHeight:'85vh',overflowY:'auto'}}>
              <div style={{fontSize:16,fontWeight:700,marginBottom:14,paddingBottom:10,borderBottom:'1px solid #e2e8f0',display:'flex',justifyContent:'space-between'}}>
                <span>견적서 공유 (읽기 전용)</span>
                <span style={{cursor:'pointer',color:'#94a3b8'}} onClick={closeShare}>✕</span>
              </div>
              <div style={{position:'relative',marginBottom:14}}>
                <input className="form-input" placeholder="이름·이메일·UID 로 검색…"
                  value={shareQuery} onChange={e=>onSearchChange(e.target.value)} autoFocus />
                {shareSearch.length > 0 && (
                  <div style={{position:'absolute',top:'100%',left:0,right:0,marginTop:4,background:'#fff',border:'1px solid #e2e8f0',borderRadius:8,boxShadow:'0 4px 12px rgba(0,0,0,0.08)',zIndex:10,maxHeight:260,overflowY:'auto'}}>
                    {shareSearch.map(u => (
                      <div key={u.id} onClick={()=>addShare(u)} style={{padding:'8px 12px',cursor:'pointer',fontSize:12,borderBottom:'1px solid #f1f5f9',display:'flex',justifyContent:'space-between',alignItems:'center'}}
                        onMouseEnter={e=>e.currentTarget.style.background='#f8fafc'} onMouseLeave={e=>e.currentTarget.style.background='#fff'}>
                        <span><strong>{u.name}</strong> <span style={{color:'#94a3b8'}}>{u.email}</span></span>
                        <span style={{color:'#94a3b8'}}>{u.org_name||''}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div style={{fontSize:11,color:'#64748b',marginBottom:6}}>공유받은 사람 ({shareList.length}명)</div>
              <div style={{display:'flex',flexDirection:'column',gap:4,maxHeight:260,overflowY:'auto'}}>
                {shareList.length === 0 ? (
                  <div style={{padding:20,textAlign:'center',color:'#94a3b8',fontSize:12}}>아직 공유된 사람이 없습니다.</div>
                ) : shareList.map(s => (
                  <div key={s.target_user_id} style={{display:'flex',alignItems:'center',gap:8,padding:'8px 10px',background:'#f8fafc',borderRadius:6}}>
                    <span style={{flex:1,fontSize:12}}><strong>{s.name}</strong> <span style={{color:'#94a3b8'}}>{s.email}</span> <span style={{color:'#94a3b8',fontSize:11}}>{s.org_name||''}</span></span>
                    <button className="btn btn-outline btn-sm" style={{color:'#dc2626',borderColor:'#fecaca'}} onClick={()=>removeShare(s.target_user_id)}>× 해제</button>
                  </div>
                ))}
              </div>
              <div style={{display:'flex',justifyContent:'flex-end',marginTop:14}}>
                <button className="btn btn-outline" onClick={closeShare}>닫기</button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* 승인 사유 모달 */}
      {showApproval && (<div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.4)',zIndex:100,display:'flex',alignItems:'center',justifyContent:'center'}} onClick={()=>setShowApproval(false)}><div onClick={e=>e.stopPropagation()} style={{background:'#fff',borderRadius:14,padding:28,width:460}}>
        <div style={{fontSize:16,fontWeight:700,marginBottom:16,paddingBottom:10,borderBottom:'1px solid #e2e8f0',display:'flex',justifyContent:'space-between'}}><span>승인 요청</span><span style={{cursor:'pointer',color:'#94a3b8'}} onClick={()=>setShowApproval(false)}>✕</span></div>
        <div style={{fontSize:13,marginBottom:12,color:'#475569'}}>이 견적서는 승인이 필요합니다.</div>
        <div className="form-group"><label className="form-label">승인 사유 (필수)</label><textarea className="form-input" rows={4} value={approvalReason} onChange={e=>setApprovalReason(e.target.value)} placeholder="승인 사유를 입력하세요..." /></div>
        <div style={{display:'flex',gap:8,justifyContent:'flex-end'}}><button className="btn btn-outline" onClick={()=>setShowApproval(false)}>취소</button><button className="btn btn-primary" disabled={!approvalReason||publishing} onClick={doPublish}>{publishing?'발행 중...':'승인 요청 및 발행'}</button></div>
      </div></div>)}

      {/* 하드웨어사양 편집 모달 */}
      {editingHwIdx !== null && items[editingHwIdx]?._product && (
        <HwSpecEditor
          item={items[editingHwIdx]}
          allParts={allParts}
          onSave={(customBom) => saveHwSpec(editingHwIdx, customBom)}
          onCancel={() => setEditingHwIdx(null)}
        />
      )}

      {/* 승인 확인 모달 */}
      {/* 묶음 컨텍스트 메뉴 (우클릭) */}
      {snippetCtxMenu && (
        <div style={{ position:'fixed', top:snippetCtxMenu.y, left:snippetCtxMenu.x, background:'#fff', border:'1px solid #e2e8f0', borderRadius:8, padding:6, boxShadow:'0 4px 12px rgba(0,0,0,0.12)', zIndex:200, minWidth:180 }}
          onClick={(e)=>e.stopPropagation()}>
          <div onClick={()=>{ setSnippetCtxMenu(null); saveCurrentAsBundle(); }}
            style={{ padding:'8px 12px', fontSize:13, cursor:'pointer', borderRadius:4 }}
            onMouseEnter={e=>e.currentTarget.style.background='#f8fafc'}
            onMouseLeave={e=>e.currentTarget.style.background=''}>
            📦 이 묶음 저장 ({items.length}개)
          </div>
          <div onClick={()=>{ setSnippetCtxMenu(null); setShowItemPicker(true); }}
            style={{ padding:'8px 12px', fontSize:13, cursor:'pointer', borderRadius:4 }}
            onMouseEnter={e=>e.currentTarget.style.background='#f8fafc'}
            onMouseLeave={e=>e.currentTarget.style.background=''}>
            📥 묶음 불러오기
          </div>
        </div>
      )}

      {showApproveModal && approvalInfo && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.4)',zIndex:100,display:'flex',alignItems:'center',justifyContent:'center'}}>
          <div style={{background:'#fff',borderRadius:14,padding:28,width:400}}>
            <div style={{fontSize:16,fontWeight:700,marginBottom:16,paddingBottom:10,borderBottom:'1px solid #e2e8f0'}}>승인 확인</div>
            <div style={{fontSize:13,marginBottom:16,color:'#475569'}}>이 견적서를 승인합니다. 비밀번호를 입력하세요.</div>
            <div className="form-group">
              <label className="form-label">비밀번호</label>
              <input className="form-input" type="password" placeholder="현재 계정 비밀번호" value={approvePassword}
                onChange={e=>setApprovePassword(e.target.value)} onKeyDown={e=>{if(e.key==='Enter'&&approvePassword) document.getElementById('btnApproveConfirm')?.click();}} />
            </div>
            <div style={{display:'flex',gap:8,justifyContent:'flex-end'}}>
              <button className="btn btn-outline" onClick={()=>{setShowApproveModal(false);setApprovePassword('');}}>취소</button>
              <button id="btnApproveConfirm" className="btn btn-primary" disabled={!approvePassword||approvalProcessing} onClick={async()=>{
                setApprovalProcessing(true);
                try {
                  const res = await api.post(`/approvals/${approvalInfo.id}/approve`,{password:approvePassword});
                  showToast(res.message||'승인 처리되었습니다.');
                  setShowApproveModal(false); setApprovePassword('');
                  setTimeout(()=>navigate('/approvals'),1000);
                } catch(e) { alert(e.message); }
                finally { setApprovalProcessing(false); }
              }}>{approvalProcessing?'처리 중...':'승인'}</button>
            </div>
          </div>
        </div>
      )}

      {/* 반려 모달 */}
      {showRejectModal && approvalInfo && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.4)',zIndex:100,display:'flex',alignItems:'center',justifyContent:'center'}}>
          <div style={{background:'#fff',borderRadius:14,padding:28,width:460}}>
            <div style={{fontSize:16,fontWeight:700,marginBottom:16,paddingBottom:10,borderBottom:'1px solid #e2e8f0'}}>반려 사유 입력</div>
            <div style={{fontSize:13,marginBottom:16,color:'#475569'}}>반려 사유를 입력해 주세요. 요청자에게 전달됩니다.</div>
            <div className="form-group">
              <label className="form-label">반려 사유 (필수)</label>
              <textarea className="form-input" rows={4} placeholder="반려 사유를 입력하세요..." value={rejectReasonText}
                onChange={e=>setRejectReasonText(e.target.value)} />
            </div>
            <div style={{display:'flex',gap:8,justifyContent:'flex-end'}}>
              <button className="btn btn-outline" onClick={()=>{setShowRejectModal(false);setRejectReasonText('');}}>취소</button>
              <button className="btn btn-danger" disabled={!rejectReasonText||approvalProcessing} style={{background:'#ef4444',color:'#fff',border:'none',opacity:rejectReasonText?1:0.5}}
                onClick={async()=>{
                  setApprovalProcessing(true);
                  try {
                    await api.post(`/approvals/${approvalInfo.id}/reject`,{reject_reason:rejectReasonText});
                    showToast('반려 처리되었습니다.');
                    setShowRejectModal(false); setRejectReasonText('');
                    setTimeout(()=>navigate('/approvals'),1000);
                  } catch(e) { alert(e.message); }
                  finally { setApprovalProcessing(false); }
                }}>{approvalProcessing?'처리 중...':'반려'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// 세부사양 (detail_sections) 인라인 편집 컴포넌트
export function SectionsEditor({ sections, onSave, onCancel }) {
  const [edit, setEdit] = useState(Array.isArray(sections) && sections.length > 0 ? [...sections] : [{title:'',content:''}]);
  const upd = (i, key, val) => { const u=[...edit]; u[i]={...u[i],[key]:val}; setEdit(u); };
  const move = (i, dir) => { const u=[...edit]; const j=i+dir; if(j<0||j>=u.length) return; [u[i],u[j]]=[u[j],u[i]]; setEdit(u); };
  return (
    <div style={{background:'#fffbeb',border:'1px solid #fde68a',borderRadius:6,padding:8,marginTop:4}}>
      {edit.map((s,i)=>(
        <div key={i} style={{display:'flex',gap:6,marginBottom:6,alignItems:'flex-start'}}>
          <div style={{flex:1,display:'flex',flexDirection:'column',gap:3}}>
            <input style={{padding:'3px 6px',border:'1px solid #d1d5db',borderRadius:4,fontSize:11,fontWeight:700}}
              value={s.title||''} onChange={e=>upd(i,'title',e.target.value)} placeholder="제목 (볼드)" />
            <textarea rows={2} style={{padding:'3px 6px',border:'1px solid #d1d5db',borderRadius:4,fontSize:11,resize:'vertical'}}
              value={s.content||''} onChange={e=>upd(i,'content',e.target.value)} placeholder="내용 (일반)" />
          </div>
          <div style={{display:'flex',flexDirection:'column',gap:2}}>
            <button style={{fontSize:10,padding:'1px 6px',border:'1px solid #d1d5db',borderRadius:4,background:'#fff',cursor:'pointer'}} disabled={i===0} onClick={()=>move(i,-1)}>↑</button>
            <button style={{fontSize:10,padding:'1px 6px',border:'1px solid #d1d5db',borderRadius:4,background:'#fff',cursor:'pointer'}} disabled={i===edit.length-1} onClick={()=>move(i,1)}>↓</button>
            <button style={{fontSize:10,padding:'1px 6px',border:'1px solid #d1d5db',borderRadius:4,background:'#fff',color:'#ef4444',cursor:'pointer'}} onClick={()=>setEdit(edit.filter((_,j)=>j!==i))}>✕</button>
          </div>
        </div>
      ))}
      <div style={{display:'flex',gap:6,marginTop:4}}>
        <button className="btn btn-outline btn-sm" style={{fontSize:10}} onClick={()=>setEdit([...edit,{title:'',content:''}])}>+ 항목 추가</button>
        <button className="btn btn-primary btn-sm" style={{fontSize:10}} onClick={()=>onSave(edit)}>저장</button>
        <button className="btn btn-outline btn-sm" style={{fontSize:10}} onClick={onCancel}>취소</button>
      </div>
    </div>
  );
}

// 성능/기능 인라인 편집 컴포넌트
export function SpecEditor({ specs, features, onSave, onCancel, section }) {
  const [editSpecs, setEditSpecs] = useState([...specs]);
  const [editFeatures, setEditFeatures] = useState([...features]);

  if (section === 'specs') return (
    <div style={{background:'#fffbeb',border:'1px solid #fde68a',borderRadius:6,padding:8,marginTop:4}}>
      {editSpecs.map((s,i)=>(
        <div key={i} style={{display:'flex',gap:6,marginBottom:4,alignItems:'center'}}>
          <input style={{flex:1,padding:'3px 6px',border:'1px solid #d1d5db',borderRadius:4,fontSize:11}} value={s.key} onChange={e=>{const u=[...editSpecs];u[i]={...u[i],key:e.target.value};setEditSpecs(u);}} />
          <input style={{flex:1,padding:'3px 6px',border:'1px solid #d1d5db',borderRadius:4,fontSize:11}} value={s.value} onChange={e=>{const u=[...editSpecs];u[i]={...u[i],value:e.target.value};setEditSpecs(u);}} />
          <span style={{color:'#ef4444',cursor:'pointer',fontSize:12}} onClick={()=>setEditSpecs(editSpecs.filter((_,j)=>j!==i))}>✕</span>
        </div>
      ))}
      <div style={{display:'flex',gap:6,marginTop:4}}>
        <button className="btn btn-outline btn-sm" style={{fontSize:10}} onClick={()=>setEditSpecs([...editSpecs,{key:'',value:''}])}>+ 추가</button>
        <button className="btn btn-primary btn-sm" style={{fontSize:10}} onClick={()=>onSave(editSpecs,editFeatures)}>저장</button>
        <button className="btn btn-outline btn-sm" style={{fontSize:10}} onClick={onCancel}>취소</button>
      </div>
    </div>
  );

  return (
    <div style={{background:'#fffbeb',border:'1px solid #fde68a',borderRadius:6,padding:8,marginTop:4}}>
      {editFeatures.map((f,i)=>(
        <div key={i} style={{display:'flex',gap:6,marginBottom:4,alignItems:'center'}}>
          <input style={{flex:1,padding:'3px 6px',border:'1px solid #d1d5db',borderRadius:4,fontSize:11}} value={f} onChange={e=>{const u=[...editFeatures];u[i]=e.target.value;setEditFeatures(u);}} />
          <span style={{color:'#ef4444',cursor:'pointer',fontSize:12}} onClick={()=>setEditFeatures(editFeatures.filter((_,j)=>j!==i))}>✕</span>
        </div>
      ))}
      <div style={{display:'flex',gap:6,marginTop:4}}>
        <button className="btn btn-outline btn-sm" style={{fontSize:10}} onClick={()=>setEditFeatures([...editFeatures,''])}>+ 추가</button>
        <button className="btn btn-primary btn-sm" style={{fontSize:10}} onClick={()=>onSave(editSpecs,editFeatures)}>저장</button>
        <button className="btn btn-outline btn-sm" style={{fontSize:10}} onClick={onCancel}>취소</button>
      </div>
    </div>
  );
}

// 하드웨어사양(BOM) 편집 모달
export function HwSpecEditor({ item, allParts, onSave, onCancel }) {
  const prod = item._product;
  const originalBom = prod?.bom || [];
  const initial = item.hw_specs?.custom_bom || originalBom.map(b => ({
    part_id: b.part_id, part_name: b.part_name, part_type: b.part_type,
    part_spec: b.part_spec, part_spec_display: b.part_spec_display,
    part_unit: b.part_unit||'', purchase_price: parseInt(b.purchase_price)||0,
    quantity: b.quantity||1, show_in_spec: b.show_in_spec,
  }));
  const [bomItems, setBomItems] = useState(initial.map(b=>({...b})));

  // part_type별 대체 부품 목록
  const partsByType = {};
  allParts.forEach(p => {
    if (!partsByType[p.part_type]) partsByType[p.part_type] = [];
    partsByType[p.part_type].push(p);
  });

  const [showAddBomPart, setShowAddBomPart] = useState(false);
  const [bomPartSearch, setBomPartSearch] = useState('');

  const addBomPart = (p) => {
    setBomItems([...bomItems, {
      part_id: p.id, part_name: p.name, part_type: p.part_type,
      part_spec: p.spec, part_spec_display: p.spec_display,
      part_unit: p.unit||'', purchase_price: parseInt(p.purchase_price)||0,
      quantity: 1, show_in_spec: true,
    }]);
    setShowAddBomPart(false);
    setBomPartSearch('');
  };

  const removeBomPart = (idx) => setBomItems(bomItems.filter((_, i) => i !== idx));

  const changePart = (idx, newPartId) => {
    const part = allParts.find(p => p.id === parseInt(newPartId));
    if (!part) return;
    setBomItems(bomItems.map((b, i) => i === idx ? {
      ...b, part_id: part.id, part_name: part.name, part_spec: part.spec,
      part_spec_display: part.spec_display,
      part_type: part.part_type, part_unit: part.unit||'', purchase_price: parseInt(part.purchase_price)||0,
    } : b));
  };

  const changeQty = (idx, qty) => {
    // 편집 중에는 빈 문자열 허용 (1을 지우고 다른 숫자 입력 가능). 저장/blur 시 1 이상으로 보정.
    setBomItems(bomItems.map((b, i) => i === idx ? { ...b, quantity: qty === '' ? '' : (parseInt(qty)||0) } : b));
  };
  const onQtyBlur = (idx) => {
    setBomItems(bomItems.map((b, i) => i === idx ? { ...b, quantity: Math.max(1, parseInt(b.quantity)||1) } : b));
  };

  const originalCost = originalBom.reduce((s, b) => s + (parseInt(b.purchase_price)||0) * (b.quantity||1), 0);
  const newCost = bomItems.reduce((s, b) => s + (parseInt(b.purchase_price)||0) * (b.quantity||1), 0);
  const costDiff = newCost - originalCost;

  return (
    <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.4)',zIndex:100,display:'flex',alignItems:'center',justifyContent:'center'}}>
      <div style={{background:'#fff',borderRadius:14,padding:28,width:700,maxHeight:'80vh',overflowY:'auto'}}>
        <div style={{fontSize:16,fontWeight:700,marginBottom:16,paddingBottom:10,borderBottom:'1px solid #e2e8f0',display:'flex',justifyContent:'space-between'}}>
          <span>하드웨어사양 수정 — {item.item_name}</span>
          <span style={{cursor:'pointer',color:'#94a3b8'}} onClick={onCancel}>✕</span>
        </div>

        <table style={{fontSize:12,width:'100%',borderCollapse:'collapse'}}>
          <thead><tr style={{background:'#f8fafc'}}>
            <th style={{padding:'8px 10px',textAlign:'left',fontWeight:600,fontSize:11,color:'#64748b',borderBottom:'2px solid #e2e8f0'}}>유형</th>
            <th style={{padding:'8px 10px',textAlign:'left',fontWeight:600,fontSize:11,color:'#64748b',borderBottom:'2px solid #e2e8f0',minWidth:200}}>부품</th>
            <th style={{padding:'8px 10px',textAlign:'center',fontWeight:600,fontSize:11,color:'#64748b',borderBottom:'2px solid #e2e8f0',width:60}}>수량</th>
            <th style={{padding:'8px 10px',textAlign:'right',fontWeight:600,fontSize:11,color:'#64748b',borderBottom:'2px solid #e2e8f0'}}>단가</th>
            <th style={{padding:'8px 10px',textAlign:'right',fontWeight:600,fontSize:11,color:'#64748b',borderBottom:'2px solid #e2e8f0'}}>소계</th>
            <th style={{width:30,borderBottom:'2px solid #e2e8f0'}}></th>
          </tr></thead>
          <tbody>
            {bomItems.map((b, idx) => {
              const alternatives = partsByType[b.part_type] || [];
              const orig = originalBom[idx];
              const changed = orig && b.part_id !== orig.part_id;
              const isAdded = idx >= originalBom.length;
              return (
                <tr key={idx} style={{borderBottom:'1px solid #f1f5f9',background:isAdded?'#f0fdf4':changed?'#eff6ff':'transparent'}}>
                  <td style={{padding:'8px 10px',fontSize:11,color:'#64748b'}}>{b.part_type}</td>
                  <td style={{padding:'8px 10px'}}>
                    {alternatives.length > 0 ? (
                      <select style={{width:'100%',padding:'4px 6px',border:'1px solid #d1d5db',borderRadius:6,fontSize:12}} value={b.part_id} onChange={e => changePart(idx, e.target.value)}>
                        {alternatives.map(p => <option key={p.id} value={p.id}>{p.name} — {p.spec} (₩{Number(p.purchase_price||0).toLocaleString()})</option>)}
                      </select>
                    ) : <span>{b.part_name} — {b.part_spec}</span>}
                  </td>
                  <td style={{padding:'8px 10px',textAlign:'center'}}>
                    <input style={{width:45,padding:'3px 5px',border:'1px solid #d1d5db',borderRadius:6,fontSize:12,textAlign:'center'}} value={b.quantity} onChange={e => changeQty(idx, e.target.value)} onBlur={() => onQtyBlur(idx)} />
                  </td>
                  <td style={{padding:'8px 10px',textAlign:'right',fontSize:11}}>₩{Number(b.purchase_price||0).toLocaleString()}</td>
                  <td style={{padding:'8px 10px',textAlign:'right',fontWeight:600}}>₩{((b.purchase_price||0)*(b.quantity||1)).toLocaleString()}</td>
                  <td style={{padding:'8px 10px'}}><span style={{color:'#ef4444',cursor:'pointer',fontSize:13,opacity:0.5}} onClick={()=>removeBomPart(idx)}>✕</span></td>
                </tr>
              );
            })}
          </tbody>
        </table>

        <button className="btn btn-outline btn-sm" style={{marginTop:10}} onClick={()=>{setShowAddBomPart(true);setBomPartSearch('');}}>+ 부품 추가</button>

        {showAddBomPart && (
          <div style={{marginTop:8,border:'1px solid #e2e8f0',borderRadius:8,padding:12,background:'#f8fafc'}}>
            <input className="form-input" style={{marginBottom:8,fontSize:12}} value={bomPartSearch} onChange={e=>setBomPartSearch(e.target.value)} placeholder="부품명, 코드, 규격 검색..." autoFocus />
            <div style={{maxHeight:200,overflowY:'auto'}}>
              <table style={{fontSize:11,width:'100%',borderCollapse:'collapse'}}>
                <thead><tr><th style={{textAlign:'left',padding:'4px 8px',borderBottom:'1px solid #e2e8f0'}}>코드</th><th style={{textAlign:'left',padding:'4px 8px',borderBottom:'1px solid #e2e8f0'}}>유형</th><th style={{textAlign:'left',padding:'4px 8px',borderBottom:'1px solid #e2e8f0'}}>부품명</th><th style={{textAlign:'left',padding:'4px 8px',borderBottom:'1px solid #e2e8f0'}}>규격</th><th style={{textAlign:'right',padding:'4px 8px',borderBottom:'1px solid #e2e8f0'}}>단가</th><th style={{padding:'4px 8px',borderBottom:'1px solid #e2e8f0'}}></th></tr></thead>
                <tbody>
                  {allParts.filter(p=>matchAnyCI(bomPartSearch, p.name, p.code, p.spec, p.part_type)).map(p=>(
                    <tr key={p.id} style={{borderBottom:'1px solid #f1f5f9'}}>
                      <td style={{padding:'4px 8px',fontFamily:'monospace'}}>{p.code}</td>
                      <td style={{padding:'4px 8px',color:'#64748b'}}>{p.part_type}</td>
                      <td style={{padding:'4px 8px'}}>{p.name}</td>
                      <td style={{padding:'4px 8px'}}>{p.spec}</td>
                      <td style={{padding:'4px 8px',textAlign:'right'}}>₩{Number(p.purchase_price||0).toLocaleString()}</td>
                      <td style={{padding:'4px 8px'}}><button className="btn btn-primary btn-sm" style={{fontSize:10,padding:'2px 8px'}} onClick={()=>addBomPart(p)}>추가</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <button className="btn btn-outline btn-sm" style={{marginTop:6,fontSize:10}} onClick={()=>setShowAddBomPart(false)}>닫기</button>
          </div>
        )}

        <div style={{marginTop:16,padding:'12px 16px',background:'#f8fafc',borderRadius:8,display:'flex',justifyContent:'space-between',alignItems:'center'}}>
          <div style={{fontSize:12}}>
            <span style={{color:'#64748b'}}>기존 원가: </span><span style={{fontWeight:600}}>₩{originalCost.toLocaleString()}</span>
            <span style={{margin:'0 12px',color:'#d1d5db'}}>→</span>
            <span style={{color:'#64748b'}}>변경 원가: </span><span style={{fontWeight:700,color:costDiff>0?'#ef4444':costDiff<0?'#22c55e':'#1e293b'}}>₩{newCost.toLocaleString()}</span>
            {costDiff !== 0 && <span style={{marginLeft:8,fontSize:11,color:costDiff>0?'#ef4444':'#22c55e'}}>({costDiff>0?'+':''}₩{costDiff.toLocaleString()})</span>}
          </div>
          <div style={{display:'flex',gap:8}}>
            <button className="btn btn-outline" onClick={onCancel}>취소</button>
            <button className="btn btn-primary" onClick={() => onSave(bomItems.map(b => ({ ...b, quantity: Math.max(1, parseInt(b.quantity)||1) })))}>적용</button>
          </div>
        </div>
      </div>
    </div>
  );
}
